/**
 * /projects/:projectId — Project detail page.
 *
 * Tabs: Overview | Phases | Budgets | Change Orders | Progress Billings
 */
import { createFileRoute, useNavigate, useRouterState } from "@tanstack/react-router";
import { useConstructionPack } from "@/lib/useConstructionPack";
import { setRouteLabel } from "@/lib/navHistory";
import * as React from "react";
import { useState, useCallback, useEffect, useMemo, Fragment, type ReactNode } from "react";
import { myEntitiesQueryOptions } from "@/lib/queries";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Autocomplete,
  createFilterOptions,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import HistoryIcon from "@mui/icons-material/History";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import { FormControlLabel, Switch } from "@mui/material";
import { api, downloadFile, errorMessage } from "@/lib/api";
import {
  projectsFullListQueryOptions,
  projectPhasesQueryOptions,
  progressBillingsQueryOptions,
  costCodesListQueryOptions,
  costCodeKeys,
  jobCostTypesQueryOptions,
  jobCostTypeKeys,
  type JobCostType,
  projectKeys,
} from "@/lib/queries";
import { useToast } from "@/components/Toaster";
import { MoneyField } from "@/components/MoneyField";
import { MoneyInput } from "@/components/MoneyInput";
import type {
  ProjectDavisBacon,
  ProjectFull,
  ProgressBillingSummary,
  ProgressBillingStatus,
  ProjectStatusStr,
  WageDetermination,
  WageDeterminationWithRates,
} from "@/lib/types";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/projects/$projectId")({
  component: ProjectDetailPage,
});

/**
 * Stable tab keys for deep-links (?tab=budgets) and in-page jumps.
 * Order must match the <Tab> list below — never hardcode bare indices
 * at call sites (they drift when tabs are inserted).
 */
const PROJECT_TABS = [
  "overview",
  "phases",
  "budgets",
  "approval",
  "change_orders",
  "progress_billings",
  "forecast",
  "wip_journal",
  "benchmarks",
  "co_passthrough",
] as const;

type ProjectTabKey = (typeof PROJECT_TABS)[number];

const PROJECT_TAB_ALIASES: Record<string, ProjectTabKey> = {
  budget: "budgets",
  job_budget: "budgets",
  project_budget: "budgets",
  eac: "forecast",
  forecast_eac: "forecast",
  wip: "wip_journal",
  cos: "change_orders",
  changeorders: "change_orders",
  billings: "progress_billings",
  progress: "progress_billings",
};

function projectTabIndex(raw: string | null | undefined): number {
  if (!raw) return 0;
  const key = raw.trim().toLowerCase().replace(/[\s-]+/g, "_");
  const aliased = PROJECT_TAB_ALIASES[key];
  const candidate =
    aliased ??
    ((PROJECT_TABS as readonly string[]).includes(key) ? (key as ProjectTabKey) : null);
  if (!candidate) return 0;
  const idx = PROJECT_TABS.indexOf(candidate);
  return idx >= 0 ? idx : 0;
}

// ── Local types (not yet in global types.ts) ─────────────────

interface Phase {
  id: string;
  project_id: string;
  code: string;
  name: string;
  parent_id: string | null;
  division: string | null;
  is_postable: boolean;
  sort_order: number;
  unit_of_measure: string | null;
  is_active: boolean;
}

interface JobBudget {
  id: string;
  project_id: string;
  phase_id: string | null;
  cost_code_id: string | null;
  cost_type: string;
  cost_type_id: string | null;
  name: string | null;
  original_amount: string;
  approved_co_amount: string;
  budgeted_hours: string | null;
  estimated_units: string | null;
  unit_cost: string | null;
  qty: string | null;
  uom: string | null;
  notes: string | null;
}

interface BudgetRevision {
  id: string;
  phase_id: string | null;
  cost_code_id: string | null;
  cost_type: string;
  revision_no: number;
  old_original_amount: string;
  new_original_amount: string;
  old_approved_co_amount: string;
  new_approved_co_amount: string;
  old_budgeted_hours: string | null;
  new_budgeted_hours: string | null;
  change_source: string;
  change_order_id: string | null;
  import_batch_id: string | null;
  revised_by: string | null;
  notes: string | null;
  created_at: string;
  phase_label: string | null;
  cost_code_label: string | null;
  revised_by_name: string | null;
  change_order_number: string | null;
}

// ── Budget approval workflow (CONSTRUCTION-BUDGET-APPROVAL-001) ─────────
// Contract for GET /api/projects/:id/budget-approval (backend Inc 1–2).
type ApproverStatus = "approved" | "current" | "waiting" | "rejected";
interface ApprovalApprover {
  tier_index: number;
  name: string;
  email: string;
  role: string;            // pm | controller | exec | owner_rep | free label
  external: boolean;
  status: ApproverStatus;
  acted_at: string | null;
  note: string | null;
}
interface ApprovalEvent {
  id: string;
  // submitted | emailed | approved | comment | rejected | excel_returned | recalled | unlocked
  kind: string;
  actor: string;
  detail: string | null;
  at: string;
}
interface BudgetApprovalState {
  budget_status: "draft" | "pending_approval" | "approved";
  request_id: string | null;
  current_tier: number | null;
  tier_count: number | null;
  budget_total: string;
  approvers: ApprovalApprover[];
  timeline: ApprovalEvent[];
  approved_at: string | null;
  approved_by_name: string | null;
}

interface ChangeOrder {
  id: string;
  project_id: string;
  co_number: string;
  co_type: string;
  status: string;
  description: string | null;
  issued_date: string | null;
  contract_amount: string;
}

// ── Status colour maps ────────────────────────────────────────

const STATUS_BG: Record<ProjectStatusStr, string> = {
  bidding: "#dbeafe", awarded: "#d1fae5", active: "#dcfce7",
  hold: "#fef3c7", closed: "#e5e7eb", cancelled: "#fee2e2",
};
const STATUS_FG: Record<ProjectStatusStr, string> = {
  bidding: "#1e40af", awarded: "#065f46", active: "#166534",
  hold: "#92400e", closed: "#374151", cancelled: "#991b1b",
};

const PB_STATUS_COLORS: Record<ProgressBillingStatus, { bg: string; fg: string }> = {
  draft:     { bg: "#e2e8f0", fg: "#475569" },
  submitted: { bg: "#dbeafe", fg: "#1e40af" },
  approved:  { bg: "#d1fae5", fg: "#065f46" },
  invoiced:  { bg: "#dcfce7", fg: "#166534" },
  paid:      { bg: "#f0fdf4", fg: "#15803d" },
  rejected:  { bg: "#fee2e2", fg: "#991b1b" },
  reversed:  { bg: "#f1f5f9", fg: "#64748b" },
};

const CO_STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
  draft:     { bg: "#e2e8f0", fg: "#475569" },
  submitted: { bg: "#dbeafe", fg: "#1e40af" },
  approved:  { bg: "#d1fae5", fg: "#065f46" },
  rejected:  { bg: "#fee2e2", fg: "#991b1b" },
  voided:    { bg: "#f3f4f6", fg: "#6b7280" },
};

// ── Money formatter ───────────────────────────────────────────

function fmt(val: string | number | null | undefined): string {
  if (val === null || val === undefined || val === "") return "—";
  return Number(val).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}

// ── Route component ───────────────────────────────────────────

function ProjectDetailPage() {
  const { enabled: constructionEnabled } = useConstructionPack();
  if (!constructionEnabled) return null;

  const { projectId } = Route.useParams();
  const navigate = useNavigate();
  // Deep-link from open_screen(project_budget) → /projects/:id?tab=budgets
  const [tab, setTab] = useState(() => {
    if (typeof window === "undefined") return 0;
    return projectTabIndex(
      new URLSearchParams(window.location.search).get("tab"),
    );
  });
  // Re-sync when ?tab= changes while already on this page (chat open_screen,
  // in-app navigate with search). Mount-only init misses that update.
  // Key only on the tab param — not the full href — so a manual tab click
  // isn't overwritten by unrelated URL noise.
  const tabFromUrl = useRouterState({
    select: (s) => {
      try {
        return new URL(s.location.href, "http://local").searchParams.get("tab");
      } catch {
        return null;
      }
    },
  });
  useEffect(() => {
    const next = projectTabIndex(tabFromUrl);
    setTab((prev) => (prev === next ? prev : next));
  }, [tabFromUrl]);

  const entitiesQ = useQuery(myEntitiesQueryOptions());
  const gmpEnabled = entitiesQ.data?.find(e => e.is_current)?.gmp_enabled ?? false;

  // Fetch the single project from the full list (no dedicated /api/projects/:id
  // query option exists yet — reuse the full list and pick by id).
  const projectsQuery = useQuery(projectsFullListQueryOptions({ limit: 500 }));
  const project = (projectsQuery.data?.items ?? []).find((p) => p.id === projectId) ?? null;

  // Label this route for the header's Recent/Most-visited menu — a job
  // page should read "26001 · CalTrans", not a generic "Projects".
  useEffect(() => {
    if (project) setRouteLabel(`/projects/${projectId}`, `${project.code} · ${project.name}`);
  }, [project, projectId]);

  // EAC summary for the always-visible header callout (so a PM sees the
  // projected final cost vs budget the moment they open a job, on every tab).
  const eacHeaderQuery = useQuery<{
    total_revised_budget: string;
    total_eac: string;
    total_variance_at_completion: string;
    manual_eac: string | null;
    lines: unknown[];
  }>({
    queryKey: ["eac", projectId],
    queryFn: () => api(`/api/projects/${projectId}/eac`),
  });

  if (projectsQuery.isLoading) {
    return (
      <Box display="flex" justifyContent="center" pt={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (!project) {
    return (
      <Box p={4}>
        <Typography color="error">Project not found.</Typography>
        <Button sx={{ mt: 2 }} onClick={() => navigate({ to: "/projects" })}>
          Back to Projects
        </Button>
      </Box>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <button
            className="text-xs text-blue-600 hover:underline"
            onClick={() => navigate({ to: "/projects" })}
          >
            ← Projects
          </button>
          <span className="font-mono text-sm font-semibold text-slate-800 dark:text-slate-200">
            {project.code}
          </span>
          <span className="text-slate-600 dark:text-slate-300 font-medium">
            {project.name}
          </span>
          <Chip
            size="small"
            label={project.status}
            sx={{
              fontSize: 10, height: 20,
              bgcolor: STATUS_BG[project.status],
              color: STATUS_FG[project.status],
              fontWeight: 600,
            }}
          />
          <div className="flex-1" />
          {/* Always-visible EAC callout — click to jump to the EAC tab */}
          {(() => {
            const e = eacHeaderQuery.data;
            const manual = e?.manual_eac != null;
            // Show when there's a computed forecast OR a manual override —
            // a budget-less project with only a manual EAC still gets the
            // callout, so the header matches the projects list.
            if (!e || (!e.lines?.length && !manual)) return null;
            const variance = Number(e.total_variance_at_completion);
            const over = variance < 0;
            // A manual override on a budget-less project has no budget to
            // compare against → variance isn't meaningful; hide the dot.
            const showVariance = e.lines?.length > 0;
            const fmt0 = (v: string | number) =>
              Number(v).toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
            return (
              <button
                onClick={() => setTab(projectTabIndex("forecast"))}
                title={manual ? "Manual EAC override — view / clear on the Forecast tab" : "View the EAC / Forecast detail"}
                className="flex items-center gap-3 rounded-md border border-slate-200 px-3 py-1 hover:bg-slate-50 dark:border-gray-700 dark:hover:bg-gray-800"
              >
                <span className="text-[10px] uppercase tracking-wide text-slate-400">Projected final cost</span>
                <span className="font-mono text-sm font-semibold inline-flex items-center gap-1">
                  {fmt0(e.total_eac)}
                  {manual && (
                    <span className="text-[9px] font-sans font-medium text-amber-700 dark:text-amber-300" title="Manually set">✎</span>
                  )}
                </span>
                {showVariance && (
                  <span className="inline-flex items-center gap-1.5">
                    <span style={{ width: 9, height: 9, borderRadius: "50%", background: over ? "#ef4444" : "#22c55e", display: "inline-block" }} />
                    <span className={`font-mono text-xs font-semibold ${over ? "text-red-600" : "text-emerald-700"}`}>
                      {over ? "" : "+"}{fmt0(e.total_variance_at_completion)}
                    </span>
                  </span>
                )}
              </button>
            );
          })()}
        </div>
      </div>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "white" }}
           className="dark:bg-gray-900">
        <Tabs
          value={tab}
          onChange={(_, v) => {
            setTab(v);
            // Mirror the tab into ?tab= (replace, not push) so refresh,
            // bookmarks, and shared links land on the same tab.
            navigate({
              to: "/projects/$projectId",
              params: { projectId },
              search: { tab: PROJECT_TABS[v] } as never,
              replace: true,
            });
          }}
          sx={{ minHeight: 40, "& .MuiTab-root": { minHeight: 40, fontSize: 13 } }}
        >
          <Tab label="Overview" />
          <Tab label="Phases" />
          <Tab label="Budgets" />
          <Tab label="Approval" />
          <Tab label="Change Orders" />
          <Tab label="Progress Billings" />
          <Tab label="EAC / Forecast" />
          <Tab label="WIP Journal" />
          <Tab label="Benchmarks" />
          <Tab label="CO Passthrough" />
          {gmpEnabled && (
            <Tab
              label="GMP"
              onClick={() => navigate({ to: "/projects/$projectId/gmp", params: { projectId } })}
            />
          )}
        </Tabs>
      </Box>

      {/* Tab panels */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {tab === 0 && <OverviewTab project={project} />}
        {tab === 1 && <PhasesTab projectId={projectId} />}
        {tab === 2 && <BudgetsTab projectId={projectId} currencyCode={project?.currency_code ?? "USD"} />}
        {tab === 3 && <BudgetApprovalTab projectId={projectId} />}
        {tab === 4 && <ChangeOrdersTab projectId={projectId} />}
        {tab === 5 && <ProgressBillingsTab projectId={projectId} />}
        {tab === 6 && <ForecastEACTab projectId={projectId} currencyCode={project?.currency_code ?? "USD"} />}
        {tab === 7 && <WipJournalTab projectId={projectId} />}
        {tab === 8 && <BenchmarksTab projectId={projectId} />}
        {tab === 9 && <COPassthroughTab projectId={projectId} />}
      </div>
    </div>
  );
}

// ── Differential stacking card ────────────────────────────────

type StackingMode = "compound" | "additive" | "max";

const STACKING_LABELS: Record<StackingMode, string> = {
  compound: "Compound (union default)",
  additive: "Additive",
  max: "Max (typical non-union)",
};

const STACKING_FORMULAS: Record<StackingMode, string> = {
  compound: "1.15 × 1.25 = 1.4375",
  additive: "(1+0.15) + (1+0.25) − 1 = 1.40",
  max: "max(1.15, 1.25) = 1.25",
};

interface AiSuggestion {
  suggested_mode: StackingMode;
  rationale: string;
  confidence: "low" | "medium" | "high";
  signals: string[];
}

function DifferentialStackingCard({ project }: { project: ProjectFull }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [mode, setMode] = useState<StackingMode>(project.differential_stacking_mode);
  const [suggestion, setSuggestion] = useState<AiSuggestion | null>(null);

  const dirty = mode !== project.differential_stacking_mode;

  const saveMut = useMutation({
    mutationFn: async () =>
      api<ProjectFull>(`/api/projects/${project.id}`, {
        method: "PATCH",
        body: { differential_stacking_mode: mode },
      }),
    onSuccess: () => {
      toast({ message: "Stacking mode saved", severity: "success" });
      qc.invalidateQueries({ queryKey: ["projects-full"] });
    },
    onError: (e: unknown) => toast({
      message: e instanceof Error ? e.message : "Save failed",
      severity: "error",
    }),
  });

  const askMut = useMutation({
    mutationFn: async () =>
      api<AiSuggestion>("/api/payroll-l2/differential-ai/suggest-stacking", {
        method: "POST",
        body: { project_id: project.id },
      }),
    onSuccess: (data) => setSuggestion(data),
    onError: (e: unknown) => toast({
      message: e instanceof Error ? e.message : "AI request failed",
      severity: "error",
    }),
  });

  const confidenceColor =
    suggestion?.confidence === "high" ? "success" :
    suggestion?.confidence === "medium" ? "warning" : "default";

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mt: 3, maxWidth: 720 }}>
      <Stack direction="row" alignItems="center" gap={0.5} mb={1}>
        <Typography variant="subtitle2" fontWeight={600}>
          Differential stacking
        </Typography>
        <Tooltip title="How shift / hazard / height pay premiums combine when more than one applies. Compound = union, Max = typical non-union.">
          <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary", fontSize: 16 }} />
        </Tooltip>
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" mb={2}>
        How multiple shift/hazard/height premiums combine on this project.
        Help: <code>docs/help/payroll-l2/differential-stacking.md</code>
      </Typography>

      <Stack direction="row" gap={2} alignItems="flex-end" mb={2}>
        <TextField
          select
          size="small"
          label="Mode"
          value={mode}
          onChange={(e) => setMode(e.target.value as StackingMode)}
          sx={{ minWidth: 260 }}
          helperText={STACKING_FORMULAS[mode]}
        >
          {(["compound", "additive", "max"] as StackingMode[]).map((m) => (
            <MenuItem key={m} value={m}>{STACKING_LABELS[m]}</MenuItem>
          ))}
        </TextField>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AutoAwesomeIcon />}
          onClick={() => askMut.mutate()}
          disabled={askMut.isPending}
        >
          {askMut.isPending ? "Thinking…" : "Ask Bynaus"}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          variant="contained"
          disabled={!dirty || saveMut.isPending}
          onClick={() => saveMut.mutate()}
        >
          {saveMut.isPending ? "Saving…" : "Save"}
        </Button>
      </Stack>

      {suggestion && (
        <Alert
          severity="info"
          icon={<AutoAwesomeIcon fontSize="small" />}
          sx={{ alignItems: "flex-start" }}
          action={
            suggestion.suggested_mode !== mode ? (
              <Button
                size="small"
                onClick={() => setMode(suggestion.suggested_mode)}
              >
                Apply
              </Button>
            ) : null
          }
        >
          <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
            <Typography variant="body2" fontWeight={600}>
              Bynaus suggests: {STACKING_LABELS[suggestion.suggested_mode]}
            </Typography>
            <Chip
              size="small"
              label={`${suggestion.confidence} confidence`}
              color={confidenceColor as "success" | "warning" | "default"}
              sx={{ height: 18, fontSize: 10 }}
            />
          </Stack>
          <Typography variant="body2" sx={{ mb: suggestion.signals.length ? 1 : 0 }}>
            {suggestion.rationale}
          </Typography>
          {suggestion.signals.length > 0 && (
            <Stack direction="row" gap={0.5} flexWrap="wrap">
              {suggestion.signals.map((s, i) => (
                <Chip key={i} label={s} size="small" sx={{ height: 18, fontSize: 10 }} />
              ))}
            </Stack>
          )}
        </Alert>
      )}
    </Paper>
  );
}

// ── Overview tab ──────────────────────────────────────────────

// ── PM Financial Status dashboard ────────────────────────────────────────
type FinancialStatus = {
  health: string;
  health_label: string;
  status: string;
  original_contract: string;
  approved_change_orders: string;
  revised_contract: string;
  revised_budget: string;
  cost_to_date: string;
  cost_to_complete: string;
  estimate_at_completion: string;
  budget_variance: string;
  pct_spent: string;
  pct_complete: string;
  projected_margin: string;
  projected_margin_pct: string;
  billed_to_date: string;
  earned_revenue: string;
  over_under_billing: string;
  has_cost_data: boolean;
};

function FinancialStatusDashboard({ projectId }: { projectId: string }) {
  const { data, isLoading } = useQuery<FinancialStatus>({
    queryKey: ["financial-status", projectId],
    queryFn: () => api(`/api/projects/${projectId}/financial-status`),
  });

  const money = (v: string | number) =>
    Number(v).toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });

  if (isLoading) return <div className="mb-4 text-sm text-slate-500">Loading financials…</div>;
  if (!data) return null;

  if (!data.has_cost_data) {
    return (
      <div className="mb-5 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600 dark:border-gray-700 dark:bg-gray-800/50 dark:text-slate-300">
        <div className="font-semibold mb-1">Financial status</div>
        No job cost yet. Add phase budgets (Budgets tab) and once costs post you'll see
        projected final cost, margin, and over/under billing here.
      </div>
    );
  }

  const variance = Number(data.budget_variance);
  const overBudget = variance < 0;
  const margin = Number(data.projected_margin);
  const overBilled = Number(data.over_under_billing);
  const pctSpent = Math.min(100, Number(data.pct_spent));
  const healthColor =
    data.health === "over_budget" ? "#ef4444" : data.health === "watch" ? "#f59e0b" : "#22c55e";

  const Tile = ({ label, value, sub, color }: { label: string; value: string; sub?: React.ReactNode; color?: string }) => (
    <div className="rounded-lg border border-slate-200 p-3 dark:border-gray-700">
      <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
      <div className="text-lg font-semibold font-mono" style={color ? { color } : undefined}>{value}</div>
      {sub != null && <div className="text-[11px] mt-0.5">{sub}</div>}
    </div>
  );

  return (
    <div className="mb-5 rounded-lg border border-slate-200 p-4 dark:border-gray-700">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">Financial status</span>
        <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium"
          style={{ background: `${healthColor}1a`, color: healthColor }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: healthColor, display: "inline-block" }} />
          {data.health_label}
        </span>
        <div className="flex-1" />
        <span className="text-xs text-slate-500">{Number(data.pct_complete).toFixed(0)}% complete</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Revised Contract" value={money(data.revised_contract)} />
        <Tile label="Projected Final Cost (EAC)" value={money(data.estimate_at_completion)}
          sub={<span className={overBudget ? "text-red-600 font-semibold" : "text-emerald-700"}>
            {overBudget ? "" : "+"}{money(data.budget_variance)} vs budget
          </span>} />
        <Tile label="Projected Margin" value={money(data.projected_margin)}
          color={margin < 0 ? "#ef4444" : undefined}
          sub={<span className="text-slate-500">{Number(data.projected_margin_pct).toFixed(1)}% of contract</span>} />
        <Tile label={overBilled >= 0 ? "Overbilled" : "Underbilled"} value={money(Math.abs(overBilled))}
          color={overBilled < 0 ? "#f59e0b" : undefined}
          sub={<span className="text-slate-500">{overBilled >= 0 ? "billed ahead of work" : "work ahead of billing"}</span>} />
      </div>

      {/* Cost-to-complete progress */}
      <div className="mt-4">
        <div className="flex justify-between text-xs text-slate-500 mb-1">
          <span>Cost to date <span className="font-mono text-slate-700 dark:text-slate-300">{money(data.cost_to_date)}</span></span>
          <span>Cost to complete <span className="font-mono text-slate-700 dark:text-slate-300">{money(data.cost_to_complete)}</span></span>
          <span>Budget <span className="font-mono text-slate-700 dark:text-slate-300">{money(data.revised_budget)}</span></span>
        </div>
        <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden dark:bg-gray-700">
          <div className="h-2.5 rounded-full"
            style={{ width: `${pctSpent}%`, background: pctSpent > 100 ? "#ef4444" : pctSpent > 90 ? "#f59e0b" : "#22c55e" }} />
        </div>
        <div className="text-[11px] text-slate-500 mt-1">{Number(data.pct_spent).toFixed(0)}% of budget spent</div>
      </div>
    </div>
  );
}

function OverviewTab({ project }: { project: ProjectFull }) {
  const rows: { label: string; value: React.ReactNode }[] = [
    { label: "Code", value: <span className="font-mono">{project.code}</span> },
    { label: "Name", value: project.name },
    { label: "Status", value: (
      <Chip size="small" label={project.status}
        sx={{
          fontSize: 10, height: 20,
          bgcolor: STATUS_BG[project.status],
          color: STATUS_FG[project.status],
          fontWeight: 600,
        }} />
    )},
    { label: "Billing method", value: project.billing_method.replace(/_/g, " ") },
    { label: "Original contract", value: `${project.currency_code} ${fmt(project.original_contract_amount)}` },
    { label: "Approved COs", value: fmt(project.approved_change_orders) },
    { label: "Revised contract", value: fmt(
        String(Number(project.original_contract_amount) + Number(project.approved_change_orders))
      )},
    { label: "Retention %", value: `${project.retention_percent}%` },
    { label: "Start date", value: project.start_date ?? "—" },
    { label: "Est. completion", value: project.estimated_completion_date ?? "—" },
    { label: "Actual completion", value: project.actual_completion_date ?? "—" },
    { label: "Currency", value: project.currency_code },
    { label: "Description", value: project.description ?? <span className="text-slate-400 italic">None</span> },
  ];

  return (
    <div className="max-w-2xl">
      <FinancialStatusDashboard projectId={project.id} />
      <Table size="small">
        <TableBody>
          {rows.map(({ label, value }) => (
            <TableRow key={label}>
              <TableCell
                sx={{ width: 180, fontWeight: 600, color: "text.secondary", fontSize: 12 }}
              >
                {label}
              </TableCell>
              <TableCell sx={{ fontSize: 13 }}>{value}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <DifferentialStackingCard project={project} />
      <DavisBaconCard projectId={project.id} />
    </div>
  );
}

// ── Davis-Bacon (certified payroll) card ─────────────────────────────────
//
// Step 4 of L4: project-level Davis-Bacon flag, WD assignment, and
// classification map (cost_code → wd_classification_code). Always rendered
// — when the certified-payroll pack is off the GET endpoint 404s and the
// card collapses to a polite "pack disabled" placeholder.
//
// UI shape:
//   • Toggle for davis_bacon_enabled
//   • WD dropdown (active WDs only, sorted by state + number)
//   • Classification map: small table of (cost_code → wd_classification)
//     with add/remove buttons
//   • Save button persists the whole assignment in one PATCH
function DavisBaconCard({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const toast = useToast();

  const dbQuery = useQuery({
    queryKey: ["payroll-l4", "project-davis-bacon", projectId],
    queryFn: async () => {
      try {
        return (await api(`/api/payroll-l4/projects/${projectId}/davis-bacon`)) as ProjectDavisBacon;
      } catch (e) {
        // 404 means pack off (or project missing on this entity).
        // The pack-off case is the common one; surface it as null.
        const status = (e as { status?: number; statusCode?: number; data?: { status?: number } }).status
          ?? (e as { statusCode?: number }).statusCode
          ?? (e as { data?: { status?: number } }).data?.status;
        if (status === 404) return null;
        throw e;
      }
    },
    retry: false,
  });

  const wdsQuery = useQuery({
    queryKey: ["payroll-l4", "wage-determinations", "active"],
    queryFn: async () =>
      (await api(`/api/payroll-l4/wage-determinations?status_filter=active`)) as WageDetermination[],
    enabled: !!dbQuery.data, // only fetch the WD list when the pack is on
  });

  const [enabled, setEnabled] = useState(false);
  const [wdId, setWdId] = useState<string>("");
  const [mapRows, setMapRows] = useState<{ cost_code: string; wd_code: string }[]>([]);
  const [hydrated, setHydrated] = useState(false);

  // One-shot hydrate when the GET resolves; subsequent edits stay local
  // until Save.
  if (dbQuery.data && !hydrated) {
    setEnabled(dbQuery.data.davis_bacon_enabled);
    setWdId(dbQuery.data.wage_determination_id ?? "");
    setMapRows(
      Object.entries(dbQuery.data.classification_map ?? {}).map(([cost_code, wd_code]) => ({
        cost_code,
        wd_code,
      })),
    );
    setHydrated(true);
  }

  // When the WD changes, prefetch its rates so the UI can offer a code dropdown
  const selectedWdQuery = useQuery({
    queryKey: ["payroll-l4", "wage-determination", wdId],
    queryFn: async () =>
      (await api(`/api/payroll-l4/wage-determinations/${wdId}`)) as WageDeterminationWithRates,
    enabled: !!wdId,
  });
  const wdRateCodes: string[] = (selectedWdQuery.data?.rates ?? [])
    .map((r) => r.classification_code ?? "")
    .filter((c) => c.length > 0);

  const save = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        davis_bacon_enabled: enabled,
        wage_determination_id: wdId || null,
        classification_map: Object.fromEntries(
          mapRows
            .map(({ cost_code, wd_code }) => [cost_code.trim(), wd_code.trim()])
            .filter(([k, v]) => k && v),
        ),
      };
      return (await api(`/api/payroll-l4/projects/${projectId}/davis-bacon`, {
        method: "PATCH",
        body: payload,
      })) as ProjectDavisBacon;
    },
    onSuccess: (data) => {
      qc.setQueryData(["payroll-l4", "project-davis-bacon", projectId], data);
      toast.success("Davis-Bacon settings saved.");
    },
    onError: (e: unknown) => {
      toast.error((e as Error).message || "Failed to save Davis-Bacon settings");
    },
  });

  if (dbQuery.isLoading) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
        <CircularProgress size={18} />
      </Paper>
    );
  }
  if (dbQuery.data === null) {
    // Pack off — quiet placeholder; admins can still see the section so
    // they know it exists, with a hint on how to enable it.
    return (
      <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Davis-Bacon / Certified Payroll
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Enable the certified-payroll pack on this entity (Setup → Entities) to
          mark projects as Davis-Bacon and assign wage determinations.
        </Typography>
      </Paper>
    );
  }

  const dirty =
    enabled !== dbQuery.data!.davis_bacon_enabled
    || wdId !== (dbQuery.data!.wage_determination_id ?? "")
    || JSON.stringify(Object.fromEntries(mapRows.map(({ cost_code, wd_code }) => [cost_code, wd_code])))
       !== JSON.stringify(dbQuery.data!.classification_map ?? {});

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="subtitle1" fontWeight={600}>
            Davis-Bacon / Certified Payroll
          </Typography>
          <HelpHint
            docPath="payroll-l4/step-4-project-db-flag.md"
            label="Davis-Bacon setup help"
          />
        </Stack>
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={(_, v) => setEnabled(v)}
            />
          }
          label={
            <Typography variant="body2">
              {enabled ? "Davis-Bacon project" : "Off"}
            </Typography>
          }
        />
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
        Flagging a project as Davis-Bacon makes the prevailing-wage compliance
        check fire at pay-run time and unlocks WH-347 generation. Requires a
        wage determination + per-cost-code classification mapping.
      </Typography>

      {/* WD assignment */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 2, mb: 2 }}>
        <TextField
          select
          label="Wage Determination"
          size="small"
          value={wdId}
          onChange={(e) => setWdId(e.target.value)}
        >
          <MenuItem value=""><em>(none)</em></MenuItem>
          {(wdsQuery.data ?? []).map((wd) => (
            <MenuItem key={wd.id} value={wd.id}>
              {wd.wd_number}
              {wd.modification_number ? ` · Mod ${wd.modification_number}` : ""}
              {" — "}{wd.state_code}
              {wd.counties.length > 0 ? ` (${wd.counties.slice(0, 2).join(", ")}${wd.counties.length > 2 ? "…" : ""})` : ""}
            </MenuItem>
          ))}
        </TextField>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          {wdId && selectedWdQuery.data && (
            <>
              <Chip
                size="small"
                label={`${selectedWdQuery.data.active_rate_count} active / ${selectedWdQuery.data.rate_count} total rates`}
                color={selectedWdQuery.data.active_rate_count > 0 ? "success" : "warning"}
                variant="outlined"
              />
              {selectedWdQuery.data.rate_count === 0 && (
                <Typography variant="caption" color="warning.main">
                  WD has no rates yet — add or upload them before flagging this project Davis-Bacon.
                </Typography>
              )}
            </>
          )}
          <Box sx={{ flex: 1 }} />
          <SuggestWdButton
            projectId={projectId}
            onPick={(wd_id) => setWdId(wd_id)}
          />
        </Stack>
      </Box>

      {/* Classification map */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">
          Classification Map
        </Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() =>
            setMapRows((rows) => [...rows, { cost_code: "", wd_code: "" }])
          }
        >
          Add row
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        Maps each project cost code (or craft) to a WD classification code. Step
        5 reads this at pay-run time to enforce the prevailing-wage rate per
        labor event.
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: "40%" }}>Cost code / craft</TableCell>
            <TableCell>WD classification code</TableCell>
            <TableCell sx={{ width: 40 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {mapRows.length === 0 && (
            <TableRow>
              <TableCell colSpan={3} sx={{ textAlign: "center", py: 2, color: "text.secondary" }}>
                No mappings yet. Click <strong>Add row</strong> (top right) to match each cost code or craft to a wage-determination classification.
              </TableCell>
            </TableRow>
          )}
          {mapRows.map((row, i) => (
            <TableRow key={i}>
              <TableCell>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="e.g. 03-3000 or CARP"
                  value={row.cost_code}
                  onChange={(e) =>
                    setMapRows((rows) =>
                      rows.map((r, j) => (j === i ? { ...r, cost_code: e.target.value } : r)),
                    )
                  }
                />
              </TableCell>
              <TableCell>
                {wdRateCodes.length > 0 ? (
                  <TextField
                    select
                    size="small"
                    fullWidth
                    value={row.wd_code}
                    onChange={(e) =>
                      setMapRows((rows) =>
                        rows.map((r, j) => (j === i ? { ...r, wd_code: e.target.value } : r)),
                      )
                    }
                  >
                    <MenuItem value=""><em>(pick a code)</em></MenuItem>
                    {wdRateCodes.map((c) => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="CARP0001 / ELEC0134-001"
                    value={row.wd_code}
                    onChange={(e) =>
                      setMapRows((rows) =>
                        rows.map((r, j) => (j === i ? { ...r, wd_code: e.target.value } : r)),
                      )
                    }
                  />
                )}
              </TableCell>
              <TableCell>
                <IconButton
                  size="small"
                  onClick={() => setMapRows((rows) => rows.filter((_, j) => j !== i))}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {save.isError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {String((save.error as Error).message)}
        </Alert>
      )}

      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button
          variant="contained"
          onClick={() => save.mutate()}
          disabled={!dirty || save.isPending}
        >
          {save.isPending ? <CircularProgress size={18} /> : "Save Davis-Bacon settings"}
        </Button>
      </Stack>
    </Paper>
  );
}

// ── Suggest WD button (Step 13.2 — AI overlay) ──────────────────────────
//
// Calls POST /payroll-l4/projects/{id}/ai/wd-suggest, surfaces the ranked
// list in a popover, and lets the admin click the top pick to fill the
// dropdown above. Falls back to a state-match heuristic when no API key
// is configured (the backend handles that).
function SuggestWdButton({
  projectId,
  onPick,
}: {
  projectId: string;
  onPick: (wdId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const suggest = useMutation({
    mutationFn: async () =>
      (await api(
        `/api/payroll-l4/projects/${projectId}/ai/wd-suggest`,
        { method: "POST", body: {} },
      )) as import("@/lib/types").WdSuggestResult,
  });
  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<AutoAwesomeIcon fontSize="small" />}
        onClick={() => {
          setOpen(true);
          if (!suggest.data) suggest.mutate();
        }}
      >
        Suggest WD
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Suggested wage determinations</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ranked by fit against this project's site state, project type, and
            recency. Click a candidate to fill the WD dropdown above.
          </Typography>
          {suggest.isPending && (
            <Stack direction="row" alignItems="center" gap={1}>
              <CircularProgress size={18} /> <Typography variant="caption">Scoring…</Typography>
            </Stack>
          )}
          {suggest.isError && (
            <Alert severity="error">
              {String((suggest.error as Error).message)}
            </Alert>
          )}
          {suggest.data && (
            <>
              <Typography variant="caption" color="text.secondary">
                {suggest.data.summary}
              </Typography>
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Rank</TableCell>
                    <TableCell>WD id</TableCell>
                    <TableCell>Fit</TableCell>
                    <TableCell>Reasoning</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {suggest.data.candidates.map((c) => (
                    <TableRow key={c.wd_id}>
                      <TableCell>{c.rank}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 11 }}>
                        {c.wd_id.slice(0, 8)}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={`${(c.fit_score * 100).toFixed(0)}%`}
                          color={c.fit_score >= 0.6 ? "success" : c.fit_score >= 0.4 ? "warning" : "default"} />
                      </TableCell>
                      <TableCell sx={{ fontSize: 11 }}>{c.reasoning}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => {
                            onPick(c.wd_id);
                            setOpen(false);
                          }}
                        >
                          Pick
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {suggest.data.candidates.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No candidates returned.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ── Phases tab ────────────────────────────────────────────────

function PhasesTab({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const phasesQuery = useQuery({
    queryKey: projectKeys.phases(projectId),
    queryFn: () => api<Phase[]>(`/api/projects/${projectId}/phases`),
    enabled: Boolean(projectId),
  });
  const [adding, setAdding] = useState(false);

  const deletePhase = useMutation({
    mutationFn: (phaseId: string) =>
      api(`/api/projects/${projectId}/phases/${phaseId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: projectKeys.phases(projectId) }),
    // A phase carrying budget lines is refused (409) — show why, don't swallow.
    onError: (err: unknown) => alert(errorMessage(err)),
  });

  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Phases</h2>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {(phasesQuery.data ?? []).length} phases
        </span>
        <div className="flex-1" />
        <Button size="small" variant="outlined" onClick={() => setAdding(true)}>
          + Add Phase
        </Button>
      </div>

      {phasesQuery.isLoading ? (
        <CircularProgress size={20} />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Code</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Division</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Unit</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Postable</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Active</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {(phasesQuery.data ?? []).map((ph) => (
              <TableRow key={ph.id} hover>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{ph.code}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{ph.name}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{ph.division ?? "—"}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{ph.unit_of_measure ?? "—"}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{ph.is_postable ? "Yes" : "No"}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{ph.is_active ? "Yes" : "No"}</TableCell>
                <TableCell align="right">
                  <button
                    type="button"
                    title={`Delete phase ${ph.code}`}
                    disabled={deletePhase.isPending}
                    onClick={() => {
                      if (confirm(`Delete phase "${ph.code} — ${ph.name}"?`)) {
                        deletePhase.mutate(ph.id);
                      }
                    }}
                    className="text-xs text-slate-400 hover:text-red-600 disabled:opacity-50 px-1"
                  >
                    Delete
                  </button>
                </TableCell>
              </TableRow>
            ))}
            {(phasesQuery.data ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <span className="text-xs text-slate-400 italic">No phases yet.</span>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {adding && (
        <AddPhaseDialog
          projectId={projectId}
          onClose={() => setAdding(false)}
        />
      )}
    </div>
  );
}

function AddPhaseDialog({
  projectId,
  onClose,
}: {
  projectId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [division, setDivision] = useState("");
  const [uom, setUom] = useState("");

  const mut = useMutation({
    mutationFn: () =>
      api<Phase>(`/api/projects/${projectId}/phases`, {
        method: "POST",
        body: { code: code.trim(), name: name.trim(), division: division || null, unit_of_measure: uom || null },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: projectKeys.phases(projectId) });
      toast.success("Phase added");
      onClose();
    },
    onError: (err: Error) => toast.error(`Failed: ${err.message}`),
  });

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Phase</DialogTitle>
      <DialogContent dividers>
        <div className="space-y-3 pt-2">
          <TextField label="Code" size="small" fullWidth value={code}
            onChange={(e) => setCode(e.target.value)} />
          <TextField label="Name" size="small" fullWidth value={name}
            onChange={(e) => setName(e.target.value)} />
          <TextField label="Division" size="small" fullWidth value={division}
            onChange={(e) => setDivision(e.target.value)}
            helperText="Optional CSI division or section" />
          <TextField label="Unit of measure" size="small" fullWidth value={uom}
            onChange={(e) => setUom(e.target.value)} />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!code || !name || mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? "Saving…" : "Add Phase"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Budgets tab ───────────────────────────────────────────────

function BudgetsTab({ projectId, currencyCode }: { projectId: string; currencyCode: string }) {
  const phasesQuery = useQuery(projectPhasesQueryOptions(projectId));
  // include_inactive: a budget line may reference a since-deactivated code, and
  // its label must still render (not a UUID). The add dialog filters to active.
  const costCodesQuery = useQuery(costCodesListQueryOptions(true));
  const costTypesQuery = useQuery(jobCostTypesQueryOptions());
  const budgetsQuery = useQuery({
    queryKey: [...projectKeys.all, "budgets", projectId],
    queryFn: () => api<JobBudget[]>(`/api/projects/${projectId}/budgets`),
    enabled: Boolean(projectId),
  });
  const [dialog, setDialog] = useState<{ mode: "add" } | { mode: "edit"; budget: JobBudget } | null>(null);
  // History panel: null = closed; {} = whole-project; {line} = one line's trail.
  const [history, setHistory] = useState<{ line?: JobBudget } | null>(null);
  // Grid = spreadsheet-style entry (default); List = the classic one-line table.
  const [view, setView] = useState<"grid" | "list">("grid");

  const phases = phasesQuery.data ?? [];
  const phaseById = new Map<string, string>();
  for (const p of phases) {
    phaseById.set(p.id, `${p.code} ${p.name}`);
  }
  const costCodes = costCodesQuery.data?.items ?? [];
  const costTypes = costTypesQuery.data ?? [];
  const codeById = new Map<string, string>();
  for (const c of costCodes) {
    codeById.set(c.id, `${c.code} ${c.name}`);
  }

  const budgets = budgetsQuery.data ?? [];
  // Show the Phase column only when the project actually uses phases — small
  // subs budget by cost type / cost code with no phases (spec §1).
  const showPhaseCol = phases.length > 0 || budgets.some((b) => b.phase_id);
  const showHoursCol = budgets.some((b) => b.budgeted_hours);
  const totalOriginal = budgets.reduce((s, b) => s + Number(b.original_amount), 0);
  const totalRevised = budgets.reduce(
    (s, b) => s + Number(b.original_amount) + Number(b.approved_co_amount), 0,
  );
  const totalHours = budgets.reduce((s, b) => s + Number(b.budgeted_hours ?? 0), 0);
  // Leading label columns before the numeric block, for colSpan on the totals row.
  const labelCols = (showPhaseCol ? 1 : 0) + 3; // + name + cost code + cost type

  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Job Budgets</h2>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {budgets.length} lines
        </span>
        <HelpHint docPath="job-budgets/entering-a-budget.md" label="Budgeting guide" />
        <div className="flex-1" />
        {/* Grid ↔ List view toggle — Grid is spreadsheet-style entry, List is
            the classic one-line-per-row table. Same data, same write path. */}
        <div className="inline-flex rounded-md border border-slate-300 dark:border-gray-600 overflow-hidden mr-1">
          <button
            type="button"
            onClick={() => setView("list")}
            className={`px-2.5 py-1 text-xs font-semibold ${view === "list" ? "bg-amber-500 text-amber-950" : "text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-800"}`}
          >
            List
          </button>
          <button
            type="button"
            onClick={() => setView("grid")}
            className={`px-2.5 py-1 text-xs font-semibold border-l border-slate-300 dark:border-gray-600 ${view === "grid" ? "bg-amber-500 text-amber-950" : "text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-800"}`}
          >
            Grid
          </button>
        </div>
        <Button
          size="small"
          variant="text"
          startIcon={<HistoryIcon />}
          disabled={budgets.length === 0}
          onClick={() => setHistory({})}
        >
          History
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setDialog({ mode: "add" })}
        >
          Add budget line
        </Button>
      </div>
      <p className="text-xs text-slate-400 italic mb-2">
        Budget by cost type, and optionally by phase and/or cost code. Phases
        are optional — leave them off if this job doesn't use them.
      </p>

      {budgetsQuery.isLoading ? (
        <CircularProgress size={20} />
      ) : view === "grid" ? (
        <BudgetGrid
          projectId={projectId}
          budgets={budgets}
          phases={phases}
          costCodes={costCodes}
          costTypes={costTypes}
          onHistory={(b) => setHistory({ line: b })}
        />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              {showPhaseCol && (
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Phase</TableCell>
              )}
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Cost code</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Cost type</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Original</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">CO adj.</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Revised</TableCell>
              {showHoursCol && (
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Budget hrs</TableCell>
              )}
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Unit cost</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right"> </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {budgets.map((b) => (
              <TableRow key={b.id} hover>
                {showPhaseCol && (
                  <TableCell
                    sx={{ fontSize: 12, cursor: "pointer" }}
                    onClick={() => setDialog({ mode: "edit", budget: b })}
                  >
                    {b.phase_id ? (phaseById.get(b.phase_id) ?? b.phase_id.slice(0, 8)) : "—"}
                  </TableCell>
                )}
                <TableCell
                  sx={{ fontSize: 12, cursor: "pointer" }}
                  onClick={() => setDialog({ mode: "edit", budget: b })}
                >
                  {b.name || <span className="text-slate-400">—</span>}
                </TableCell>
                <TableCell
                  sx={{ fontSize: 12, cursor: "pointer" }}
                  onClick={() => setDialog({ mode: "edit", budget: b })}
                >
                  {b.cost_code_id ? (codeById.get(b.cost_code_id) ?? b.cost_code_id.slice(0, 8)) : "—"}
                </TableCell>
                <TableCell
                  sx={{ fontSize: 12, cursor: "pointer" }}
                  onClick={() => setDialog({ mode: "edit", budget: b })}
                >
                  {b.cost_type}
                </TableCell>
                <TableCell sx={{ fontSize: 12, fontFamily: "monospace" }} align="right">
                  {fmt(b.original_amount)}
                </TableCell>
                <TableCell sx={{ fontSize: 12, fontFamily: "monospace" }} align="right">
                  {fmt(b.approved_co_amount)}
                </TableCell>
                <TableCell sx={{ fontSize: 12, fontFamily: "monospace" }} align="right">
                  {fmt(String(Number(b.original_amount) + Number(b.approved_co_amount)))}
                </TableCell>
                {showHoursCol && (
                  <TableCell sx={{ fontSize: 12, fontFamily: "monospace" }} align="right">
                    {b.budgeted_hours ? Number(b.budgeted_hours).toLocaleString() : "—"}
                  </TableCell>
                )}
                <TableCell sx={{ fontSize: 12, fontFamily: "monospace" }} align="right">
                  {b.unit_cost ? fmt(b.unit_cost) : "—"}
                </TableCell>
                <TableCell align="right" sx={{ p: 0 }}>
                  <Tooltip title="Change history">
                    <IconButton size="small" onClick={() => setHistory({ line: b })}>
                      <HistoryIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {budgets.length === 0 && (
              <TableRow>
                <TableCell colSpan={labelCols + 5}>
                  <span className="text-xs text-slate-400 italic">No budget lines yet.</span>
                </TableCell>
              </TableRow>
            )}
            {budgets.length > 0 && (
              <TableRow sx={{ "& td": { borderTop: 2, borderColor: "divider", fontWeight: 700 } }}>
                <TableCell colSpan={labelCols} sx={{ fontSize: 12 }}>Totals</TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }} align="right">
                  {fmt(String(totalOriginal))}
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }} align="right">
                  {fmt(String(totalRevised - totalOriginal))}
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }} align="right">
                  {fmt(String(totalRevised))}
                </TableCell>
                {showHoursCol && (
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }} align="right">
                    {totalHours ? totalHours.toLocaleString() : "—"}
                  </TableCell>
                )}
                <TableCell colSpan={2} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {dialog && (
        <BudgetLineDialog
          projectId={projectId}
          currencyCode={currencyCode}
          phases={phases}
          costCodes={costCodes}
          costTypes={costTypes}
          existing={dialog.mode === "edit" ? dialog.budget : null}
          onClose={() => setDialog(null)}
        />
      )}
      {history && (
        <BudgetHistoryDialog
          projectId={projectId}
          line={history.line ?? null}
          onClose={() => setHistory(null)}
        />
      )}
    </div>
  );
}

const COST_TYPES = ["labor", "material", "subcontract", "equipment", "other", "burden"];

// Short column labels for the wide Budget Grid.
const CT_META: { key: string; label: string }[] = [
  { key: "labor", label: "Labor" },
  { key: "material", label: "Material" },
  { key: "subcontract", label: "Sub" },
  { key: "equipment", label: "Equip" },
  { key: "other", label: "Other" },
  { key: "burden", label: "Burden" },
];

/**
 * Cost-type picker with inline create/remove.
 *
 * A custom type must say what it "counts as": the canonical base is the GL
 * posting dimension, so travel counts as Other and posts to the Other job-cost
 * account. The customer owns the naming; the ledger keeps its accounts.
 */
function CostTypePicker({
  value, onChange, costTypes, disabled,
}: {
  value: string;                       // job_cost_types.id
  onChange: (id: string) => void;
  costTypes: JobCostType[];
  disabled?: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [code, setCode] = useState("");
  const [base, setBase] = useState("other");

  const createMut = useMutation({
    mutationFn: () =>
      api<JobCostType>("/api/job-cost-types", {
        method: "POST",
        body: { code: code.trim(), name: code.trim(), base_cost_type: base },
      }),
    onSuccess: (ct) => {
      qc.invalidateQueries({ queryKey: jobCostTypeKeys.all });
      onChange(ct.id);
      setAdding(false);
      setCode("");
      toast.success(`Cost type ${ct.code} added`);
    },
    onError: (e: Error) => toast.error(errorMessage(e)),
  });

  const removeMut = useMutation({
    mutationFn: (id: string) => api(`/api/job-cost-types/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: jobCostTypeKeys.all });
      toast.success("Cost type removed");
    },
    onError: (e: Error) => toast.error(errorMessage(e)),
  });

  if (adding) {
    return (
      <div className="flex gap-2 items-start">
        <TextField
          size="small" label="New cost type" value={code} autoFocus
          onChange={(e) => setCode(e.target.value)}
          inputProps={{ maxLength: 40 }}
          placeholder="e.g. TRAVEL"
        />
        <TextField
          select size="small" label="Counts as" value={base} sx={{ minWidth: 150 }}
          onChange={(e) => setBase(e.target.value)}
          helperText="Which job-cost account it posts to"
        >
          {COST_TYPES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </TextField>
        <Button
          size="small" variant="contained" sx={{ mt: 0.5 }}
          disabled={code.trim() === "" || createMut.isPending}
          onClick={() => createMut.mutate()}
        >
          Add
        </Button>
        <Button size="small" sx={{ mt: 0.5 }} onClick={() => setAdding(false)}>
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <TextField
      select label="Cost type" size="small" fullWidth value={value}
      disabled={disabled}
      onChange={(e) => {
        if (e.target.value === "__add__") { setAdding(true); return; }
        onChange(e.target.value);
      }}
    >
      {costTypes.map((c) => (
        <MenuItem key={c.id} value={c.id}>
          <span style={{ flex: 1 }}>
            {c.name}
            {c.base_cost_type !== c.code.toLowerCase() && (
              <span style={{ color: "#94a3b8", fontSize: 11 }}> · posts as {c.base_cost_type}</span>
            )}
          </span>
          <span
            role="button"
            aria-label={`Remove ${c.code}`}
            onClick={(e) => {
              e.stopPropagation();
              if (confirm(`Remove cost type ${c.code}? Budget lines already using it keep posting as ${c.base_cost_type}.`)) {
                removeMut.mutate(c.id);
              }
            }}
            style={{ color: "#cbd5e1", fontSize: 13, paddingLeft: 8, cursor: "pointer" }}
          >
            ✕
          </span>
        </MenuItem>
      ))}
      <MenuItem value="__add__">
        <em>+ Add a cost type…</em>
      </MenuItem>
    </TextField>
  );
}

type PickableCode = { id: string; code: string; name: string; is_active?: boolean };

/**
 * Cost-code picker that can CREATE and DEACTIVATE codes without leaving the
 * budget screen.
 *
 * WHY: cost codes are the customer's own vocabulary and differ on every job —
 * the pilot CFO's words were "need to be able to add cost codes ... add and
 * delete them right there". Sending someone to a separate admin page mid-budget
 * is how a budget ends up coded to whatever code happened to exist already.
 *
 * "Delete" deactivates (the API's soft delete). A code is referenced by
 * journal lines, budgets and time entries, so destroying it would orphan
 * posted history; deactivating removes it from every picker while existing
 * lines keep reading correctly.
 */
function CostCodePicker({
  value, onChange, costCodes, disabled, label,
}: {
  value: PickableCode | null;
  onChange: (v: PickableCode | null) => void;
  costCodes: PickableCode[];
  disabled?: boolean;
  label: string;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const filter = createFilterOptions<PickableCode>();
  const active = costCodes.filter((c) => c.is_active !== false);

  const createMut = useMutation({
    mutationFn: (code: string) =>
      // Name defaults to the code — same convention the importer uses when it
      // meets an unknown code. Renaming lives on the cost-codes admin page.
      api<PickableCode>("/api/cost-codes", {
        method: "POST",
        body: { code, name: code },
      }),
    onSuccess: (cc) => {
      qc.invalidateQueries({ queryKey: costCodeKeys.all });
      onChange(cc);
      toast.success(`Cost code ${cc.code} added`);
    },
    onError: (e: Error) => toast.error(errorMessage(e)),
  });

  const deactivateMut = useMutation({
    mutationFn: (id: string) => api(`/api/cost-codes/${id}`, { method: "DELETE" }),
    onSuccess: (_r, id) => {
      qc.invalidateQueries({ queryKey: costCodeKeys.all });
      if (value?.id === id) onChange(null);
      toast.success("Cost code removed");
    },
    onError: (e: Error) => toast.error(errorMessage(e)),
  });

  return (
    <Autocomplete
      size="small"
      options={active}
      value={value}
      disabled={disabled || createMut.isPending}
      getOptionLabel={(o) => (o.name && o.name !== o.code ? `${o.code} ${o.name}` : o.code)}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      filterOptions={(opts, params) => {
        const filtered = filter(opts, params);
        const typed = params.inputValue.trim();
        // Offer to create only when nothing matches that code (case-insensitive,
        // matching how the importer resolves codes — otherwise the picker would
        // invite a duplicate the API then refuses).
        const exists = opts.some(
          (o) => o.code.trim().toLowerCase() === typed.toLowerCase(),
        );
        if (typed !== "" && !exists) {
          filtered.push({ id: "__new__", code: typed, name: "" });
        }
        return filtered;
      }}
      onChange={(_, v) => {
        if (v && v.id === "__new__") createMut.mutate(v.code);
        else onChange(v);
      }}
      renderOption={(props, o) => {
        const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: string };
        if (o.id === "__new__") {
          return (
            <li key={key} {...rest}>
              <span style={{ fontSize: 13 }}>
                Add <strong>{o.code}</strong> as a new cost code
              </span>
            </li>
          );
        }
        return (
          <li key={key} {...rest} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
            <span style={{ fontSize: 13 }}>
              <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{o.code}</span>
              {o.name && o.name !== o.code ? <span style={{ color: "#94a3b8" }}> {o.name}</span> : null}
            </span>
            <Tooltip title="Remove this cost code">
              <span
                role="button"
                aria-label={`Remove ${o.code}`}
                onClick={(e) => {
                  // Stop the click selecting the option we are removing.
                  e.stopPropagation();
                  if (confirm(`Remove cost code ${o.code}? Budget lines and posted history already using it are not changed — it just stops appearing in pickers.`)) {
                    deactivateMut.mutate(o.id);
                  }
                }}
                style={{ color: "#cbd5e1", fontSize: 14, lineHeight: 1, padding: "0 2px", cursor: "pointer" }}
              >
                ✕
              </span>
            </Tooltip>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText="Type a code that doesn't exist yet to add it."
        />
      )}
    />
  );
}

interface GridWorkItem {
  phase_id: string | null;
  cost_code_id: string | null;
  byType: Record<string, JobBudget>;
}

/**
 * Budget Grid — spreadsheet-style entry, the alternative to the List table.
 *
 * One row per work item (phase × cost code), one column per cost type. It's a
 * wide view over the long `job_budgets` storage: each amount cell maps to one
 * line, and every edit routes through the SAME `PUT /budgets` upsert the List
 * view and the importer use — so revision history and the mixed-grain guard are
 * unchanged. Structure-first: add cost-code rows as empty skeletons (client
 * side) and fill amounts later; a `job_budgets` line is created lazily when the
 * first amount is entered on that (phase, cost_code) row.
 */
function BudgetGrid({
  projectId, budgets, phases, costCodes, costTypes, onHistory,
}: {
  projectId: string;
  budgets: JobBudget[];
  phases: { id: string; code: string; name: string }[];
  costCodes: { id: string; code: string; name: string }[];
  costTypes: JobCostType[];
  onHistory: (b: JobBudget) => void;
}) {
  const qc = useQueryClient();
  // Columns are the entity's own cost types, so a customer-defined "Travel"
  // gets its own column instead of being invisible or lumped under Other.
  const columns = costTypes.length
    ? costTypes.map((c) => ({ key: c.id, label: c.name, base: c.base_cost_type }))
    : CT_META.map((c) => ({ key: c.key, label: c.label, base: c.key }));
  const toast = useToast();

  const money = (n: number) => (n ? `$${Math.round(n).toLocaleString()}` : "");
  const phaseName = (id: string | null) => {
    if (!id) return "No phase";
    const p = phases.find((x) => x.id === id);
    return p ? `${p.code} ${p.name}` : id.slice(0, 8);
  };
  const codeName = (id: string | null) => {
    if (!id) return null;
    const c = costCodes.find((x) => x.id === id);
    return c ? { code: c.code, name: c.name } : { code: id.slice(0, 8), name: "" };
  };

  const hasPhases = phases.length > 0 || budgets.some((b) => b.phase_id);

  const items = useMemo(() => {
    const m = new Map<string, GridWorkItem>();
    for (const b of budgets) {
      const key = `${b.phase_id ?? ""}|${b.cost_code_id ?? ""}`;
      let it = m.get(key);
      if (!it) { it = { phase_id: b.phase_id, cost_code_id: b.cost_code_id, byType: {} }; m.set(key, it); }
      // Keyed by the customer's cost type, not the canonical base — two custom
      // types can share a base and must occupy different columns. Lines from
      // before this feature fall back to the base string.
      it.byType[b.cost_type_id ?? b.cost_type] = b;
    }
    return m;
  }, [budgets]);

  // Structure-first skeletons — client-only rows that carry no line until filled.
  const [skeletons, setSkeletons] = useState<{ phase_id: string | null; cost_code_id: string }[]>([]);
  useEffect(() => {
    setSkeletons((sk) => sk.filter((s) => !items.has(`${s.phase_id ?? ""}|${s.cost_code_id}`)));
  }, [items]);

  const [editing, setEditing] = useState<{ key: string; ct: string } | null>(null);
  // Editing the row's NAME (not a cost-type amount cell).
  const [editingName, setEditingName] = useState<string | null>(null);
  const [addPhase, setAddPhase] = useState<string>("");

  const cellMut = useMutation({
    mutationFn: (v: { phase_id: string | null; cost_code_id: string | null; cost_type: string; cost_type_id: string | null; original_amount: string }) =>
      api<JobBudget>(`/api/projects/${projectId}/budgets`, { method: "PUT", body: v }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...projectKeys.all, "budgets", projectId] });
      qc.invalidateQueries({ queryKey: projectKeys.all });
      qc.invalidateQueries({ queryKey: ["eac", projectId] });
      qc.invalidateQueries({ queryKey: ["financial-status", projectId] });
      qc.invalidateQueries({ queryKey: ["projects", "eac-summary"] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  // In the grid the ROW is the scope of work ("Level 3 corridor drywall") and
  // the columns are how its cost splits by type — which is how estimators
  // think. So a row's name labels every line on that row, and renaming sends
  // one upsert per cost type present, amount unchanged.
  const nameMut = useMutation({
    mutationFn: async (v: { item: GridWorkItem; name: string | null }) => {
      const lines = Object.values(v.item.byType);
      for (const ln of lines) {
        await api<JobBudget>(`/api/projects/${projectId}/budgets`, {
          method: "PUT",
          body: {
            phase_id: v.item.phase_id,
            cost_code_id: v.item.cost_code_id,
            cost_type: ln.cost_type,
            original_amount: ln.original_amount,
            name: v.name,
          },
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...projectKeys.all, "budgets", projectId] });
      qc.invalidateQueries({ queryKey: projectKeys.all });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  /** Distinct names across the row's lines — normally one. */
  function rowName(r: GridWorkItem): string {
    const seen = [...new Set(
      Object.values(r.byType).map((b) => (b.name ?? "").trim()).filter(Boolean),
    )];
    return seen.join(" · ");
  }

  function commitName(item: GridWorkItem, raw: string) {
    setEditingName(null);
    const next = raw.trim();
    if (next === rowName(item)) return;                 // no-op
    if (Object.keys(item.byType).length === 0) return;  // skeleton: nothing to name yet
    nameMut.mutate({ item, name: next === "" ? null : next });
  }

  function commit(item: GridWorkItem, ctKey: string, raw: string) {
    setEditing(null);
    const cleaned = raw.replace(/[^0-9.]/g, "");
    const val = cleaned === "" ? "0" : cleaned;
    const ex = item.byType[ctKey];
    // No-op guard: unchanged existing value, or a still-empty cell left blank.
    if (ex ? Number(ex.original_amount) === Number(val) : Number(val) === 0) return;
    // ctKey is the customer's cost-type row id; the server derives the
    // canonical base from it. Fall back to treating the key as the base for
    // an entity with no cost types yet (columns come from CT_META then).
    const col = columns.find((c) => c.key === ctKey);
    cellMut.mutate({
      phase_id: item.phase_id,
      cost_code_id: item.cost_code_id,
      cost_type: col?.base ?? ctKey,
      cost_type_id: col && col.key !== col.base ? col.key : null,
      original_amount: val,
    });
  }

  type Row = GridWorkItem & { key: string; skeleton?: boolean };
  const rows: Row[] = [];
  for (const [key, it] of items) rows.push({ key, ...it });
  for (const s of skeletons) rows.push({ key: `${s.phase_id ?? ""}|${s.cost_code_id}`, phase_id: s.phase_id, cost_code_id: s.cost_code_id, byType: {}, skeleton: true });

  const phaseOrder = (id: string | null) => { const i = phases.findIndex((p) => p.id === id); return i < 0 ? 999 : i; };
  rows.sort((a, b) =>
    phaseOrder(a.phase_id) - phaseOrder(b.phase_id) ||
    (codeName(a.cost_code_id)?.code ?? "~").localeCompare(codeName(b.cost_code_id)?.code ?? "~"));

  const totals: Record<string, number> = {};
  columns.forEach((c) => { totals[c.key] = 0; });
  let totCO = 0;
  budgets.forEach((b) => {
    // Grid columns are keyed by the entity cost-type id when one exists.
    // Imported budget lines carry both that id and their canonical base type;
    // accumulating by the base string made every custom-column footer render
    // $0 even though the row amounts and persisted budget were correct.
    const columnKey = b.cost_type_id ?? b.cost_type;
    totals[columnKey] = (totals[columnKey] ?? 0) + Number(b.original_amount);
    totCO += Number(b.approved_co_amount);
  });
  const totOrig = columns.reduce((s, c) => s + totals[c.key], 0);

  // Group rows under phase subheaders (only when the job uses phases).
  const groups: { phase_id: string | null; rows: Row[] }[] = [];
  for (const r of rows) {
    let g = groups.find((x) => x.phase_id === r.phase_id);
    if (!g) { g = { phase_id: r.phase_id, rows: [] }; groups.push(g); }
    g.rows.push(r);
  }

  const rowCO = (r: Row) => columns.reduce((s, c) => s + Number(r.byType[c.key]?.approved_co_amount ?? 0), 0);
  const rowOrig = (r: Row) => columns.reduce((s, c) => s + Number(r.byType[c.key]?.original_amount ?? 0), 0);
  const totalCols = 1 + columns.length + 3;

  const headSx = { fontWeight: 700, fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".4px", whiteSpace: "nowrap", p: "8px 10px" } as const;
  const numHead = { ...headSx, textAlign: "right" } as const;

  function amountCell(r: Row) {
    return columns.map((c) => {
      const ex = r.byType[c.key];
      const isEditing = editing?.key === r.key && editing.ct === c.key;
      return (
        <TableCell
          key={c.key}
          align="right"
          sx={{
            fontSize: 12, fontFamily: "monospace", p: 0, cursor: "text",
            "&:hover": { bgcolor: "action.hover" },
          }}
          onClick={() => setEditing({ key: r.key, ct: c.key })}
        >
          {isEditing ? (
            <TextField
              autoFocus variant="standard" defaultValue={ex ? ex.original_amount : ""}
              onBlur={(e) => commit(r, c.key, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                else if (e.key === "Escape") setEditing(null);
              }}
              inputProps={{ style: { textAlign: "right", fontFamily: "monospace", fontSize: 12, padding: "6px 10px" } }}
              sx={{ width: "100%" }}
            />
          ) : (
            <div style={{ padding: "6px 10px", minWidth: 64 }}>
              {ex ? money(Number(ex.original_amount)) : <span style={{ color: "var(--mui-palette-text-disabled, #bbb)" }}>–</span>}
            </div>
          )}
        </TableCell>
      );
    });
  }

  const availableCodes = costCodes; // full list; picking one adds a skeleton row

  return (
    <div style={{ overflowX: "auto", border: "1px solid", borderColor: "rgba(0,0,0,.08)", borderRadius: 8 }}>
      <Table size="small" sx={{ minWidth: 880 }}>
        <TableHead>
          <TableRow sx={{ "& th": { bgcolor: "action.hover" } }}>
            <TableCell sx={{ ...headSx, position: "sticky", left: 0, zIndex: 2, bgcolor: "background.paper" }}>Cost code / name</TableCell>
            {columns.map((c) => <TableCell key={c.key} sx={numHead}>{c.label}</TableCell>)}
            <TableCell sx={numHead}>CO adj.</TableCell>
            <TableCell sx={numHead}>Revised</TableCell>
            <TableCell sx={headSx} />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={totalCols}>
              <span className="text-xs text-slate-400 italic">No budget lines yet — add a cost-code row below, then fill in the amounts.</span>
            </TableCell></TableRow>
          )}
          {groups.map((g) => (
            <Fragment key={g.phase_id ?? "none"}>
              {hasPhases && (
                <TableRow sx={{ "& td": { bgcolor: "action.hover", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: ".4px" } }}>
                  <TableCell colSpan={totalCols}>
                    {phaseName(g.phase_id)}
                    <span style={{ fontFamily: "monospace", color: "#94a3b8", fontWeight: 600, marginLeft: 8, textTransform: "none" }}>
                      · {money(g.rows.reduce((s, r) => s + rowOrig(r), 0)) || "$0"}
                    </span>
                  </TableCell>
                </TableRow>
              )}
              {g.rows.map((r) => {
                const cn = codeName(r.cost_code_id);
                return (
                  <TableRow key={r.key} hover>
                    <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap", position: "sticky", left: 0, zIndex: 1, bgcolor: "background.paper" }}>
                      {cn ? (
                        <>
                          <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{cn.code}</span>
                          {/* Auto-created codes get name = code; printing both
                              rendered "TAPING Taping" — noise the customer
                              flagged on this exact grid. */}
                          {cn.name.trim().toLowerCase() !== cn.code.trim().toLowerCase() && (
                            <> <span style={{ color: "#94a3b8" }}>{cn.name}</span></>
                          )}
                        </>
                      ) : <span style={{ color: "#94a3b8" }}>—</span>}
                      {editingName === r.key ? (
                        <input
                          autoFocus
                          defaultValue={rowName(r)}
                          maxLength={200}
                          onBlur={(e) => commitName(r, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                            if (e.key === "Escape") setEditingName(null);
                          }}
                          style={{
                            display: "block", marginTop: 2, width: "100%", minWidth: 160,
                            font: "inherit", fontSize: 11, padding: "1px 4px",
                            border: "1px solid #cbd5e1", borderRadius: 4,
                          }}
                        />
                      ) : (
                        <div
                          onClick={() => !r.skeleton && setEditingName(r.key)}
                          title={r.skeleton ? "Enter an amount first" : "Click to name this line"}
                          style={{
                            fontSize: 11, marginTop: 1,
                            color: rowName(r) ? "#475569" : "#cbd5e1",
                            cursor: r.skeleton ? "default" : "text",
                          }}
                        >
                          {rowName(r) || (r.skeleton ? "" : "+ name")}
                        </div>
                      )}
                    </TableCell>
                    {amountCell(r)}
                    <TableCell align="right" sx={{ fontSize: 12, fontFamily: "monospace", color: rowCO(r) < 0 ? "#dc2626" : rowCO(r) > 0 ? "#15803d" : "#94a3b8" }}>
                      {rowCO(r) ? (rowCO(r) > 0 ? "+" : "") + money(rowCO(r)) : "—"}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 12, fontFamily: "monospace", fontWeight: 700 }}>
                      {money(rowOrig(r) + rowCO(r)) || "$0"}
                    </TableCell>
                    <TableCell align="right" sx={{ p: 0 }}>
                      {!r.skeleton && (
                        <Tooltip title="Change history">
                          <IconButton size="small" onClick={() => { const any = Object.values(r.byType)[0]; if (any) onHistory(any); }}>
                            <HistoryIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </Fragment>
          ))}
          {rows.length > 0 && (
            <TableRow sx={{ "& td": { borderTop: 2, borderColor: "divider", fontWeight: 700, fontFamily: "monospace", fontSize: 12, bgcolor: "action.hover" } }}>
              <TableCell sx={{ fontFamily: "inherit", textTransform: "uppercase", letterSpacing: ".4px", fontSize: 11, position: "sticky", left: 0, bgcolor: "background.paper" }}>Total</TableCell>
              {columns.map((c) => <TableCell key={c.key} align="right">{money(totals[c.key]) || "$0"}</TableCell>)}
              <TableCell align="right">{totCO ? money(totCO) : "—"}</TableCell>
              <TableCell align="right">{money(totOrig + totCO) || "$0"}</TableCell>
              <TableCell />
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Structure-first: add a cost-code row (empty skeleton) to fill later. */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderTop: "1px solid rgba(0,0,0,.08)", flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>+ Add cost-code row:</span>
        {hasPhases && (
          <TextField select size="small" label="Phase" value={addPhase} onChange={(e) => setAddPhase(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">No phase</MenuItem>
            {phases.map((p) => <MenuItem key={p.id} value={p.id}>{p.code} {p.name}</MenuItem>)}
          </TextField>
        )}
        <div style={{ minWidth: 280 }}>
          <CostCodePicker
            label="Cost code"
            value={null}
            costCodes={availableCodes}
            onChange={(val) => {
              if (!val) return;
              const ph = addPhase === "" ? null : addPhase;
              const key = `${ph ?? ""}|${val.id}`;
              if (items.has(key) || skeletons.some((s) => `${s.phase_id ?? ""}|${s.cost_code_id}` === key)) {
                toast.error("That cost code row already exists"); return;
              }
              setSkeletons((sk) => [...sk, { phase_id: ph, cost_code_id: val.id }]);
            }}
          />
        </div>
        <span style={{ fontSize: 11, color: "#94a3b8" }}>Rows added here stay empty until you type an amount.</span>
      </div>
    </div>
  );
}

function BudgetLineDialog({
  projectId,
  currencyCode,
  phases,
  costCodes,
  costTypes,
  existing,
  onClose,
}: {
  projectId: string;
  currencyCode: string;
  // Minimal structural shape — accepts both Phase and PhaseSummary.
  phases: { id: string; code: string; name: string; is_postable: boolean }[];
  costCodes: { id: string; code: string; name: string; is_active?: boolean }[];
  costTypes: JobCostType[];
  existing: JobBudget | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const postablePhases = phases.filter((p) => p.is_postable);
  // Phase is optional. "" = no phase (a project-level line).
  const [phaseId, setPhaseId] = useState(existing?.phase_id ?? "");
  const [costCode, setCostCode] = useState<{ id: string; code: string; name: string } | null>(
    existing?.cost_code_id
      ? costCodes.find((c) => c.id === existing.cost_code_id) ?? null
      : null,
  );
  // What the estimator calls this line. Editable on an existing line too —
  // it labels the row, it isn't part of the line's identity.
  const [name, setName] = useState(existing?.name ?? "");
  const [costTypeId, setCostTypeId] = useState<string>(
    existing?.cost_type_id
      ?? costTypes.find((c) => c.base_cost_type === (existing?.cost_type ?? "labor"))?.id
      ?? costTypes[0]?.id
      ?? "",
  );
  const [amount, setAmount] = useState(existing?.original_amount ?? "");
  const [hours, setHours] = useState(existing?.budgeted_hours ?? "");
  const [estUnits, setEstUnits] = useState(existing?.estimated_units ?? "");
  const [unitCost, setUnitCost] = useState(existing?.unit_cost ?? "");
  const [reason, setReason] = useState("");

  const mut = useMutation({
    mutationFn: () =>
      api<JobBudget>(`/api/projects/${projectId}/budgets`, {
        method: "PUT",
        // On edit the identity is locked — send it VERBATIM from the record, not
        // from picker state. Deriving cost_code_id from the async, active-only
        // cost-codes list can resolve to null (still loading, or the code was
        // deactivated), which would silently retarget the upsert to a different
        // line identity (§13.1 review finding). qty/uom/notes are omitted so the
        // backend's PATCH semantics preserve them (the dialog doesn't expose them).
        body: existing
          ? {
              phase_id: existing.phase_id,
              cost_code_id: existing.cost_code_id,
              cost_type: existing.cost_type,
              name: name.trim() === "" ? null : name.trim(),
              original_amount: amount.trim() === "" ? "0" : amount.trim(),
              budgeted_hours: hours.trim() === "" ? null : hours.trim(),
              estimated_units: estUnits.trim() === "" ? null : estUnits.trim(),
              unit_cost: unitCost.trim() === "" ? null : unitCost.trim(),
              reason: reason.trim() === "" ? null : reason.trim(),
            }
          : {
              phase_id: phaseId === "" ? null : phaseId,
              cost_code_id: costCode?.id ?? null,
              cost_type_id: costTypeId || null,
              // Still sent so a caller without a resolved type stays valid;
              // when cost_type_id is set the server derives the base from it.
              cost_type:
                costTypes.find((c) => c.id === costTypeId)?.base_cost_type ?? "labor",
              name: name.trim() === "" ? null : name.trim(),
              original_amount: amount.trim() === "" ? "0" : amount.trim(),
              budgeted_hours: hours.trim() === "" ? null : hours.trim(),
              estimated_units: estUnits.trim() === "" ? null : estUnits.trim(),
              unit_cost: unitCost.trim() === "" ? null : unitCost.trim(),
              reason: reason.trim() === "" ? null : reason.trim(),
            },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...projectKeys.all, "budgets", projectId] });
      // The budget feeds the project's EAC / financial-status views — these are
      // NOT under the "projects" key prefix, so invalidate them explicitly.
      qc.invalidateQueries({ queryKey: projectKeys.all });
      qc.invalidateQueries({ queryKey: ["eac", projectId] });
      qc.invalidateQueries({ queryKey: ["financial-status", projectId] });
      qc.invalidateQueries({ queryKey: ["projects", "eac-summary"] });
      toast.success(existing ? "Budget line updated" : "Budget line added");
      onClose();
    },
    onError: (err: Error) => toast.error(`Failed: ${errorMessage(err)}`),
  });

  // The (phase, cost code, cost type) triple is the line's identity — the same
  // triple upserts (revises) the existing line, so lock it when editing to
  // avoid a surprise second line.
  const identityLocked = existing !== null;
  const amountNum = Number(amount);
  const invalidAmount = amount.trim() !== "" && (Number.isNaN(amountNum) || amountNum < 0);
  const hoursNum = Number(hours);
  const invalidHours = hours.trim() !== "" && (Number.isNaN(hoursNum) || hoursNum < 0);
  const selectedBase =
    costTypes.find((c) => c.id === costTypeId)?.base_cost_type ?? "labor";
  const hoursRelevant = selectedBase === "labor" || selectedBase === "burden";

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{existing ? "Edit budget line" : "Add budget line"}</DialogTitle>
      <DialogContent dividers>
        <div className="space-y-3 pt-2">
          <TextField
            label="Name" size="small" fullWidth value={name}
            onChange={(e) => setName(e.target.value)}
            inputProps={{ maxLength: 200 }}
            placeholder="e.g. Level 3 corridor drywall"
            helperText="What you call this line. The coding below is what actual costs match on."
          />
          <TextField
            select label="Phase (optional)" size="small" fullWidth value={phaseId}
            disabled={identityLocked}
            onChange={(e) => setPhaseId(e.target.value)}
            helperText={postablePhases.length === 0 ? "No phases on this project — that's fine, leave blank." : undefined}
          >
            <MenuItem value=""><em>None (project-level)</em></MenuItem>
            {postablePhases.map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.code} {p.name}</MenuItem>
            ))}
          </TextField>
          <CostCodePicker
            label="Cost code (optional)"
            value={costCode}
            onChange={setCostCode}
            costCodes={costCodes}
            disabled={identityLocked}
          />
          <CostTypePicker
            value={costTypeId}
            onChange={setCostTypeId}
            costTypes={costTypes}
            disabled={identityLocked}
          />
          <div className="flex gap-3">
            <MoneyField
              label="Budget amount" size="small" fullWidth currency={currencyCode}
              value={amount}
              onChange={setAmount}
              error={invalidAmount}
              helperText={invalidAmount ? "Enter a non-negative number" : "Original budgeted cost"}
            />
            <TextField
              label="Budget hours" size="small" fullWidth value={hours}
              onChange={(e) => setHours(e.target.value)}
              error={invalidHours}
              helperText={
                invalidHours ? "Enter a non-negative number"
                : hoursRelevant ? "Labor hours (early-warning signal)"
                : "Usually for labor / burden"
              }
            />
          </div>
          <div className="flex gap-3">
            <TextField
              label="Est. units (optional)" size="small" fullWidth value={estUnits}
              onChange={(e) => setEstUnits(e.target.value)}
            />
            <MoneyField
              label="Unit cost (optional)" size="small" fullWidth currency={currencyCode}
              value={unitCost}
              onChange={setUnitCost}
            />
          </div>
          {existing && (
            <TextField
              label="Reason for change (optional)" size="small" fullWidth value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. buyout savings, scope correction"
              helperText="Recorded on the budget's change history."
              multiline
              minRows={3}
            />
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={invalidAmount || invalidHours || mut.isPending}
          onClick={() => mut.mutate()}
        >
          {mut.isPending ? "Saving…" : existing ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Budget change history ─────────────────────────────────────

const BUDGET_SOURCE_LABEL: Record<string, string> = {
  manual: "Manual", change_order: "Change order", import: "Import", agent: "Agent",
};

function BudgetHistoryDialog({
  projectId,
  line,
  onClose,
}: {
  projectId: string;
  line: JobBudget | null;
  onClose: () => void;
}) {
  const revisionsQuery = useQuery({
    queryKey: [...projectKeys.all, "budget-revisions", projectId],
    queryFn: () => api<BudgetRevision[]>(`/api/projects/${projectId}/budget-revisions`),
  });
  // Filter to one line client-side so NULL phase / cost code match exactly —
  // an optional query param can't distinguish "no phase" from "any phase".
  const revisions = (revisionsQuery.data ?? []).filter((r) =>
    !line
      ? true
      : r.phase_id === line.phase_id
        && r.cost_code_id === line.cost_code_id
        && r.cost_type === line.cost_type,
  );

  const scopeOf = (r: BudgetRevision) => {
    const parts = [r.phase_label, r.cost_code_label].filter(Boolean);
    parts.push(r.cost_type);
    return parts.join(" · ");
  };
  const money = (v: string) => `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {line ? "Line change history" : "Budget change history"}
      </DialogTitle>
      <DialogContent dividers>
        {revisionsQuery.isLoading ? (
          <CircularProgress size={20} />
        ) : revisions.length === 0 ? (
          <p className="text-xs text-slate-400 italic">No changes recorded yet.</p>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>When</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Who</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Source</TableCell>
                {!line && (
                  <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Line</TableCell>
                )}
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Original</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Hours</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Reason</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {revisions.map((r) => {
                const amtChanged = r.old_original_amount !== r.new_original_amount;
                const hrsChanged = (r.old_budgeted_hours ?? null) !== (r.new_budgeted_hours ?? null);
                return (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>
                      {new Date(r.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{r.revised_by_name ?? "—"}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          r.change_order_number
                            ? `CO ${r.change_order_number}`
                            : (BUDGET_SOURCE_LABEL[r.change_source] ?? r.change_source)
                        }
                      />
                    </TableCell>
                    {!line && (
                      <TableCell sx={{ fontSize: 12 }}>{scopeOf(r)}</TableCell>
                    )}
                    <TableCell sx={{ fontSize: 12, fontFamily: "monospace" }} align="right">
                      {amtChanged
                        ? `${money(r.old_original_amount)} → ${money(r.new_original_amount)}`
                        : money(r.new_original_amount)}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, fontFamily: "monospace" }} align="right">
                      {hrsChanged
                        ? `${r.old_budgeted_hours ?? "—"} → ${r.new_budgeted_hours ?? "—"}`
                        : (r.new_budgeted_hours ?? "—")}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{r.notes ?? "—"}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Change Orders tab ─────────────────────────────────────────

function ChangeOrdersTab({ projectId }: { projectId: string }) {
  const coQuery = useQuery({
    queryKey: [...projectKeys.all, "change-orders", projectId],
    queryFn: () =>
      api<ChangeOrder[]>(`/api/projects/${projectId}/change-orders`),
    enabled: Boolean(projectId),
  });

  const cos = coQuery.data ?? [];

  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Change Orders</h2>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {cos.length} COs
        </span>
      </div>

      {coQuery.isLoading ? (
        <CircularProgress size={20} />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>CO #</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Issued</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cos.map((co) => {
              const col = CO_STATUS_COLORS[co.status] ?? CO_STATUS_COLORS.draft;
              return (
                <TableRow key={co.id} hover>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{co.co_number}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{co.co_type}</TableCell>
                  <TableCell>
                    <span
                      className="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase"
                      style={{ background: col.bg, color: col.fg }}
                    >
                      {co.status}
                    </span>
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, maxWidth: 300 }}>
                    <span className="truncate block">{co.description ?? "—"}</span>
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{co.issued_date ?? "—"}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }} align="right">
                    {fmt(co.contract_amount)}
                  </TableCell>
                </TableRow>
              );
            })}
            {cos.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <span className="text-xs text-slate-400 italic">No change orders yet.</span>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// ── Progress Billings tab ─────────────────────────────────────

function ProgressBillingsTab({ projectId }: { projectId: string }) {
  const pbQuery = useQuery(progressBillingsQueryOptions(projectId));
  const pbs = pbQuery.data?.items ?? [];
  const totalBillings = pbQuery.data?.total ?? 0;

  const handleDownload = useCallback((pb: ProgressBillingSummary) => {
    downloadFile(
      `/api/projects/progress-billings/${pb.id}/pdf`,
      `G702-G703-App${pb.application_no}-${pb.billing_date}.pdf`,
    ).catch((err: Error) => alert(err.message));
  }, []);

  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
          Progress Billings
        </h2>
        <span className="text-xs text-slate-400 dark:text-slate-500">(AIA G702/G703)</span>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {totalBillings} application{totalBillings !== 1 ? "s" : ""}
        </span>
      </div>

      {pbQuery.isLoading ? (
        <CircularProgress size={20} />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>App #</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Period</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Billed date</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">
                Completed &amp; stored
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">
                Current due
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">
                Less retention
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {pbs.map((pb) => {
              const col = PB_STATUS_COLORS[pb.status] ?? PB_STATUS_COLORS.draft;
              return (
                <TableRow key={pb.id} hover>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                    #{pb.application_no}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>
                    {pb.period_from} → {pb.period_to}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{pb.billing_date}</TableCell>
                  <TableCell>
                    <span
                      className="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase"
                      style={{ background: col.bg, color: col.fg }}
                    >
                      {pb.status}
                    </span>
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }} align="right">
                    {fmt(Number(pb.total_completed_to_date) + Number(pb.total_stored_materials))}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }} align="right">
                    {fmt(pb.current_due)}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }} align="right">
                    {fmt(pb.less_retention)}
                  </TableCell>
                  <TableCell>
                    <button
                      title="Download G702/G703 PDF"
                      className="text-blue-600 hover:text-blue-800 text-[11px] font-medium"
                      onClick={() => handleDownload(pb)}
                    >
                      PDF
                    </button>
                  </TableCell>
                </TableRow>
              );
            })}
            {pbs.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  <span className="text-xs text-slate-400 italic">
                    No progress billing applications yet.
                  </span>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// ── Forecast / EAC Tab ────────────────────────────────────────
function ForecastEACTab({ projectId, currencyCode }: { projectId: string; currencyCode: string }) {
  const qc = useQueryClient();
  const { data: eac, isLoading } = useQuery<{
    project_id: string;
    lines: {
      phase_id: string;
      phase_code: string | null;
      phase_name: string | null;
      cost_type: string;
      revised_budget: string;
      actual_cost: string;
      pct_complete: string;
      etc_amount: string;
      eac: string;
      variance_at_completion: string;
    }[];
    total_revised_budget: string;
    total_actual_cost: string;
    total_eac: string;
    total_variance_at_completion: string;
    computed_eac: string;
    computed_variance_at_completion: string;
    manual_eac: string | null;
  }>({
    queryKey: ["eac", projectId],
    queryFn: () => api(`/api/projects/${projectId}/eac`),
  });

  const [editing, setEditing] = React.useState<{
    phase_id: string;
    cost_type: string;
    etc_amount: string;
    percent_complete: string;
    notes: string;
  } | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [clearing, setClearing] = React.useState(false);

  // Clear the project-level manual EAC override (set inline on the projects
  // list). Reverts every surface to the bottom-up computed EAC.
  async function clearManualEac() {
    setClearing(true);
    try {
      await api(`/api/projects/${projectId}`, {
        method: "PATCH",
        body: { manual_eac: null },
      });
      await qc.invalidateQueries({ queryKey: ["eac", projectId] });
      await qc.invalidateQueries({ queryKey: ["financial-status", projectId] });
      await qc.invalidateQueries({ queryKey: ["projects", "eac-summary"] });
    } finally {
      setClearing(false);
    }
  }

  const fmt = (v: string | number) =>
    Number(v).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

  const varianceColor = (v: string) =>
    Number(v) < 0 ? "text-red-600 font-semibold" : "text-emerald-700";

  async function saveForeast() {
    if (!editing) return;
    setSaving(true);
    try {
      await api(`/api/projects/${projectId}/forecast`, {
        method: "PUT",
        body: {
          phase_id: editing.phase_id,
          cost_type: editing.cost_type,
          etc_amount: editing.etc_amount === "" ? null : Number(editing.etc_amount),
          percent_complete: editing.percent_complete === "" ? null : Number(editing.percent_complete),
          notes: editing.notes || null,
        },
      });
      await qc.invalidateQueries({ queryKey: ["eac", projectId] });
      setEditing(null);
    } finally {
      setSaving(false);
    }
  }

  if (isLoading) return <div className="p-4 text-sm text-slate-500">Loading…</div>;
  if (!eac) return null;

  const hasManual = eac.manual_eac != null;

  // No per-phase cost data. If a PM set a manual EAC on the list, surface it
  // (and let them clear it) so this tab matches what the list shows; otherwise
  // show the usual "add budgets" empty state.
  if (!eac.lines.length) {
    if (!hasManual) {
      return (
        <div className="p-8 text-center text-sm text-slate-500">
          No cost forecast yet. Add phases and budgets to this project (Budgets tab),
          and once costs post you'll see the projected final cost (EAC) here.
        </div>
      );
    }
    return (
      <div className="p-4">
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/40 dark:bg-amber-900/10">
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              Manual EAC override — {fmt(eac.total_eac)}
            </div>
            <div className="text-xs text-amber-800/80 mt-1 dark:text-amber-200/70">
              Set inline on the Projects list. This project has no phase budgets yet,
              so there's no computed forecast to compare against. Add budgets (Budgets tab)
              for a bottom-up EAC, or clear the override below.
            </div>
          </div>
          <button
            className="text-xs px-3 py-1.5 rounded border border-amber-300 bg-white hover:bg-amber-100 disabled:opacity-50 dark:bg-transparent dark:text-amber-200"
            onClick={clearManualEac}
            disabled={clearing}
          >
            {clearing ? "Clearing…" : "Clear override"}
          </button>
        </div>
      </div>
    );
  }

  const totalVariance = Number(eac.total_variance_at_completion);
  const overBudget = totalVariance < 0;
  const pctOver = Number(eac.total_revised_budget) > 0
    ? Math.abs(totalVariance) / Number(eac.total_revised_budget) * 100
    : 0;

  return (
    <div className="p-4 space-y-4">
      {/* Summary bar */}
      <div className="flex gap-6 text-sm">
        <div>
          <span className="text-slate-500">Revised Budget</span>
          <div className="font-semibold text-base">{fmt(eac.total_revised_budget)}</div>
        </div>
        <div>
          <span className="text-slate-500">Projected Final Cost (EAC)</span>
          <div className="font-semibold text-base flex items-center gap-1.5">
            {fmt(eac.total_eac)}
            {hasManual && (
              <span
                className="text-[10px] font-medium text-amber-700 bg-amber-100 rounded px-1 py-0.5 dark:bg-amber-900/30 dark:text-amber-300"
                title="Manually set on the Projects list"
              >
                ✎ manual
              </span>
            )}
          </div>
        </div>
        <div>
          <span className="text-slate-500">{overBudget ? "Projected Over Budget" : "Projected Margin"}</span>
          <div className={`font-semibold text-base ${varianceColor(eac.total_variance_at_completion)}`}>
            {overBudget ? "" : "+"}{fmt(eac.total_variance_at_completion)}
            <span className="ml-1 text-xs font-normal">
              ({pctOver.toFixed(1)}% {overBudget ? "over" : "under"})
            </span>
          </div>
        </div>
      </div>

      {/* Manual override banner — explains why the EAC differs from the
          bottom-up phase total, and lets the PM revert. */}
      {hasManual && (
        <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-xs dark:border-amber-900/40 dark:bg-amber-900/10">
          <span className="text-amber-900 dark:text-amber-200">
            <strong>Manual EAC override active.</strong> The EAC above is a
            project-level override set on the Projects list. The phases below
            sum to a computed{" "}
            <span className="font-mono font-semibold">{fmt(eac.computed_eac)}</span>{" "}
            ({Number(eac.computed_variance_at_completion) < 0 ? "" : "+"}
            {fmt(eac.computed_variance_at_completion)} vs budget).
          </span>
          <div className="flex-1" />
          <button
            className="px-3 py-1 rounded border border-amber-300 bg-white hover:bg-amber-100 disabled:opacity-50 dark:bg-transparent dark:text-amber-200"
            onClick={clearManualEac}
            disabled={clearing}
          >
            {clearing ? "Clearing…" : "Clear override"}
          </button>
        </div>
      )}

      {/* Inline edit panel */}
      {editing && (
        <div className="border border-blue-200 rounded-lg p-4 bg-blue-50 space-y-3">
          <div className="text-sm font-semibold text-blue-800">
            Update Forecast — Phase {editing.phase_id} / {editing.cost_type}
          </div>
          <div className="flex gap-3 flex-wrap">
            <div>
              <label className="text-xs text-slate-600 block mb-1">ETC Amount</label>
              <MoneyInput
                currency={currencyCode}
                className="border rounded px-2 py-1 text-sm w-36"
                value={editing.etc_amount}
                onChange={(v) => setEditing({ ...editing, etc_amount: v })}
                placeholder="leave blank = auto"
              />
            </div>
            <div>
              <label className="text-xs text-slate-600 block mb-1">% Complete</label>
              <input
                type="number"
                className="border rounded px-2 py-1 text-sm w-24"
                value={editing.percent_complete}
                onChange={(e) => setEditing({ ...editing, percent_complete: e.target.value })}
                min={0}
                max={100}
              />
            </div>
            <div className="flex-1 min-w-48">
              <label className="text-xs text-slate-600 block mb-1">Notes</label>
              <input
                type="text"
                className="border rounded px-2 py-1 text-sm w-full"
                value={editing.notes}
                onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              className="bg-blue-600 text-white text-xs px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50"
              onClick={saveForeast}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save & recalculate EAC"}
            </button>
            <button
              className="text-xs px-3 py-1.5 rounded border hover:bg-slate-50"
              onClick={() => setEditing(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* EAC table */}
      <Table size="small">
        <TableHead>
          <TableRow sx={{ background: "#f8fafc" }}>
            <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Phase</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Type</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Revised Budget</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Actual Cost</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Cost to Complete</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">EAC</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Over / Under</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Spent</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {eac.lines.map((ln) => {
            const pct = Math.min(100, Number(ln.pct_complete));
            const barColor = pct > 90 ? "#ef4444" : pct > 75 ? "#f59e0b" : "#22c55e";
            return (
              <TableRow key={`${ln.phase_id}-${ln.cost_type}`} hover>
                <TableCell sx={{ fontSize: 12 }}>
                  <span className="font-mono text-xs text-slate-500">{ln.phase_code}</span>{" "}
                  {ln.phase_name}
                </TableCell>
                <TableCell sx={{ fontSize: 12 }}>{ln.cost_type}</TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }} align="right">
                  {fmt(ln.revised_budget)}
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }} align="right">
                  {fmt(ln.actual_cost)}
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }} align="right">
                  {fmt(ln.etc_amount)}
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }} align="right">
                  {fmt(ln.eac)}
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }} align="right">
                  <span className={varianceColor(ln.variance_at_completion)}>
                    {Number(ln.variance_at_completion) >= 0 ? "+" : ""}{fmt(ln.variance_at_completion)}
                  </span>
                </TableCell>
                <TableCell sx={{ width: 80 }}>
                  <div className="flex items-center gap-1">
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-2 rounded-full"
                        style={{ width: `${pct}%`, background: barColor }}
                      />
                    </div>
                    <span className="text-[10px] text-slate-500 w-8 text-right">{pct.toFixed(0)}%</span>
                  </div>
                </TableCell>
                <TableCell>
                  <button
                    className="text-xs text-blue-600 hover:text-blue-800"
                    onClick={() =>
                      setEditing({
                        phase_id: ln.phase_id,
                        cost_type: ln.cost_type,
                        etc_amount: ln.etc_amount ?? "",
                        percent_complete: "",
                        notes: "",
                      })
                    }
                  >
                    Edit
                  </button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

// ── WIP Journal Tab ───────────────────────────────────────────
function WipJournalTab({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const [periodId, setPeriodId] = React.useState("");
  const [reverseExisting, setReverseExisting] = React.useState(false);
  const [posting, setPosting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const { data: periods } = useQuery<{ id: string; name: string; start_date: string }[]>({
    queryKey: ["periods"],
    queryFn: () => api("/api/periods"),
  });

  const { data: runs, isLoading } = useQuery<{
    id: string;
    period_id: string;
    cost_to_date: string;
    revised_contract: string;
    pct_complete: string;
    revenue_earned: string;
    billed_to_date: string;
    over_billed: string;
    under_billed: string;
    created_at: string;
  }[]>({
    queryKey: ["wip-runs", projectId],
    queryFn: () => api(`/api/projects/${projectId}/wip-journal-runs`),
  });

  const fmt = (v: string | number) =>
    Number(v).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

  async function postJournal() {
    if (!periodId) return;
    setPosting(true);
    setError(null);
    try {
      await api(`/api/projects/${projectId}/wip-journal`, {
        method: "POST",
        body: { period_id: periodId, reverse_existing: reverseExisting },
      });
      await qc.invalidateQueries({ queryKey: ["wip-runs", projectId] });
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(msg ?? "Failed to post WIP journal");
    } finally {
      setPosting(false);
    }
  }

  const periodName = (id: string) =>
    periods?.find((p) => p.id === id)?.name ?? id.slice(0, 8);

  return (
    <div className="p-4 space-y-4">
      {/* Post panel */}
      <div className="border rounded-lg p-4 bg-slate-50 space-y-3">
        <div className="text-sm font-semibold text-slate-700">Post WIP Journal Entry</div>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="text-xs text-slate-600 block mb-1">Period</label>
            <select
              className="border rounded px-2 py-1.5 text-sm bg-white"
              value={periodId}
              onChange={(e) => setPeriodId(e.target.value)}
            >
              <option value="">— select period —</option>
              {periods?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={reverseExisting}
              onChange={(e) => setReverseExisting(e.target.checked)}
            />
            Reverse existing entry first
          </label>
          <button
            className="bg-indigo-600 text-white text-sm px-4 py-1.5 rounded hover:bg-indigo-700 disabled:opacity-50"
            onClick={postJournal}
            disabled={posting || !periodId}
          >
            {posting ? "Posting…" : "Post Journal"}
          </button>
        </div>
        {error && <div className="text-xs text-red-600">{error}</div>}
      </div>

      {/* Run history */}
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Run History</div>
      {isLoading ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: "#f8fafc" }}>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Period</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Cost to Date</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Revised Contract</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">% Complete</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Revenue Earned</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Billed to Date</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Under-billed</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Over-billed</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Run At</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {runs?.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell sx={{ fontSize: 12 }}>{periodName(r.period_id)}</TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }} align="right">{fmt(r.cost_to_date)}</TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }} align="right">{fmt(r.revised_contract)}</TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }} align="right">
                  {(Number(r.pct_complete) * 100).toFixed(1)}%
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }} align="right">{fmt(r.revenue_earned)}</TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }} align="right">{fmt(r.billed_to_date)}</TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }} align="right">
                  <span className={Number(r.under_billed) > 0 ? "text-amber-600 font-semibold" : ""}>
                    {fmt(r.under_billed)}
                  </span>
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }} align="right">
                  <span className={Number(r.over_billed) > 0 ? "text-red-600 font-semibold" : ""}>
                    {fmt(r.over_billed)}
                  </span>
                </TableCell>
                <TableCell sx={{ fontSize: 11, color: "#94a3b8" }}>
                  {new Date(r.created_at).toLocaleString()}
                </TableCell>
              </TableRow>
            ))}
            {(!runs || runs.length === 0) && (
              <TableRow>
                <TableCell colSpan={9}>
                  <span className="text-xs text-slate-400 italic">No WIP journal runs yet.</span>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// ── Benchmarks Tab ────────────────────────────────────────────
function BenchmarksTab({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const [refreshing, setRefreshing] = React.useState(false);

  const { data: flags, isLoading } = useQuery<{
    phase_code: string;
    cost_type: string;
    current_ratio: string;
    projected_final_ratio: string;
    benchmark_p75: string;
    benchmark_p90: string;
    severity: "watch" | "warning";
  }[]>({
    queryKey: ["benchmark-flags", projectId],
    queryFn: () =>
      api(`/api/projects/${projectId}/benchmark-flags`).then((r: { flags?: unknown[] }) => (r.flags ?? []) as never),
  });

  async function refreshBenchmarks() {
    setRefreshing(true);
    try {
      await api("/api/projects/benchmarks/refresh", { method: "POST" });
      await qc.invalidateQueries({ queryKey: ["benchmark-flags", projectId] });
    } finally {
      setRefreshing(false);
    }
  }

  const severityChip = (s: "watch" | "warning") =>
    s === "warning" ? (
      <span className="inline-block rounded-full px-2 py-0.5 text-[10px] font-bold bg-red-100 text-red-700">
        WARNING
      </span>
    ) : (
      <span className="inline-block rounded-full px-2 py-0.5 text-[10px] font-bold bg-amber-100 text-amber-700">
        WATCH
      </span>
    );

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-700">Cost Benchmark Flags</div>
          <div className="text-xs text-slate-500 mt-0.5">
            Phases trending above p75 (watch) or p90 (warning) vs. similar closed projects.
          </div>
        </div>
        <button
          className="bg-slate-700 text-white text-xs px-3 py-1.5 rounded hover:bg-slate-900 disabled:opacity-50"
          onClick={refreshBenchmarks}
          disabled={refreshing}
        >
          {refreshing ? "Refreshing…" : "Refresh Benchmarks"}
        </button>
      </div>

      {isLoading ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : !flags || flags.length === 0 ? (
        <div className="text-sm text-slate-400 italic">No phases flagged — all within historical benchmarks.</div>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: "#f8fafc" }}>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Phase Code</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Cost Type</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Severity</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Current Ratio</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Projected Final</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">p75 Benchmark</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">p90 Benchmark</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11, width: 140 }}>vs. p90</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {flags.map((f) => {
              const ratio = Number(f.projected_final_ratio);
              const p90 = Number(f.benchmark_p90) || 1;
              const barPct = Math.min(100, (ratio / (p90 * 1.3)) * 100);
              const barColor = f.severity === "warning" ? "#ef4444" : "#f59e0b";
              return (
                <TableRow key={`${f.phase_code}-${f.cost_type}`} hover>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{f.phase_code}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{f.cost_type}</TableCell>
                  <TableCell>{severityChip(f.severity)}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }} align="right">
                    {Number(f.current_ratio).toFixed(3)}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }} align="right">
                    {ratio.toFixed(3)}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }} align="right">
                    {Number(f.benchmark_p75).toFixed(3)}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }} align="right">
                    {p90.toFixed(3)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-2 rounded-full"
                          style={{ width: `${barPct}%`, background: barColor }}
                        />
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// ── CO Passthrough Tab ────────────────────────────────────────
function COPassthroughTab({ projectId }: { projectId: string }) {
  const qc = useQueryClient();

  const { data: rules, isLoading: rulesLoading } = useQuery<{
    id: string;
    subcontract_id: string;
    phase_id: string | null;
    cost_type: string | null;
    markup_percent: string;
    is_active: boolean;
    notes: string | null;
  }[]>({
    queryKey: ["passthrough-rules", projectId],
    queryFn: () =>
      api(`/api/projects/${projectId}/passthrough-rules`),
  });

  const { data: subs } = useQuery<{ id: string; subcontract_no: string; description: string | null }[]>({
    queryKey: ["subcontracts", projectId],
    queryFn: () =>
      api(`/api/projects/subcontracts/all?project_id=${projectId}`),
  });

  const { data: phases } = useQuery<{ id: string; code: string; name: string }[]>({
    queryKey: ["phases", projectId],
    queryFn: () =>
      api(`/api/projects/${projectId}/phases`),
  });

  const [showAddRule, setShowAddRule] = React.useState(false);
  const [ruleForm, setRuleForm] = React.useState({
    subcontract_id: "",
    markup_percent: "0",
    phase_id: "",
    cost_type: "",
    notes: "",
  });
  const [savingRule, setSavingRule] = React.useState(false);
  const [ruleError, setRuleError] = React.useState<string | null>(null);

  async function saveRule() {
    if (!ruleForm.subcontract_id) return;
    setSavingRule(true);
    setRuleError(null);
    try {
      await api(`/api/projects/${projectId}/passthrough-rules`, {
        method: "POST",
        body: {
          subcontract_id: ruleForm.subcontract_id,
          markup_percent: Number(ruleForm.markup_percent),
          phase_id: ruleForm.phase_id || null,
          cost_type: ruleForm.cost_type || null,
          notes: ruleForm.notes || null,
        },
      });
      await qc.invalidateQueries({ queryKey: ["passthrough-rules", projectId] });
      setShowAddRule(false);
      setRuleForm({ subcontract_id: "", markup_percent: "0", phase_id: "", cost_type: "", notes: "" });
    } catch (e: unknown) {
      setRuleError(errorMessage(e) ?? "Failed to save rule");
    } finally {
      setSavingRule(false);
    }
  }

  async function deleteRule(ruleId: string) {
    if (!window.confirm("Delete this passthrough rule?")) return;
    await api(`/api/projects/passthrough-rules/${ruleId}`, { method: "DELETE" });
    await qc.invalidateQueries({ queryKey: ["passthrough-rules", projectId] });
  }

  const { data: cos } = useQuery<{
    id: string;
    co_number: string;
    co_type: string;
    status: string;
    description: string | null;
  }[]>({
    queryKey: ["change-orders", projectId],
    queryFn: () =>
      api(`/api/projects/${projectId}/change-orders`),
  });

  const approvedOCOs = cos?.filter((c) => c.co_type === "OCO" && c.status === "approved") ?? [];

  const [selectedCOId, setSelectedCOId] = React.useState<string>("");
  const [generating, setGenerating] = React.useState(false);
  const [genError, setGenError] = React.useState<string | null>(null);

  const { data: coPassthroughs, isLoading: ptLoading } = useQuery<{
    id: string;
    sub_co_id: string;
    sub_co_number: string | null;
    sub_co_status: string | null;
    sub_co_contract_amount: string | null;
    sub_co_description: string | null;
    rule_id: string | null;
    triggered_by: string | null;
    created_at: string | null;
  }[]>({
    queryKey: ["co-passthrough", selectedCOId],
    queryFn: () =>
      api(`/api/projects/change-orders/${selectedCOId}/passthrough`),
    enabled: !!selectedCOId,
  });

  async function runGenerate() {
    if (!selectedCOId) return;
    setGenerating(true);
    setGenError(null);
    try {
      const res = await api<{ generated_count?: number }>(
        `/api/projects/change-orders/${selectedCOId}/generate-passthrough`,
        { method: "POST" },
      );
      await qc.invalidateQueries({ queryKey: ["co-passthrough", selectedCOId] });
      if (res.generated_count === 0) {
        setGenError("No new passthroughs generated — all rules already processed or no rules matched.");
      }
    } catch (e: unknown) {
      setGenError(errorMessage(e) ?? "Failed to generate passthrough");
    } finally {
      setGenerating(false);
    }
  }

  const subName = (id: string) => {
    const s = subs?.find((s) => s.id === id);
    return s ? `${s.subcontract_no}${s.description ? ` — ${s.description}` : ""}` : id.slice(0, 8);
  };

  const phaseName = (id: string | null) => {
    if (!id) return <span className="text-slate-400 italic">any phase</span>;
    const p = phases?.find((p) => p.id === id);
    return p ? `${p.code} ${p.name}` : id.slice(0, 8);
  };

  const statusChip = (s: string | null) => {
    if (!s) return null;
    const colors: Record<string, string> = {
      draft: "bg-slate-100 text-slate-600",
      submitted: "bg-blue-100 text-blue-700",
      approved: "bg-green-100 text-green-700",
      rejected: "bg-red-100 text-red-700",
    };
    return (
      <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${colors[s] ?? "bg-slate-100 text-slate-600"}`}>
        {s.toUpperCase()}
      </span>
    );
  };

  const fmt = (v: string | number | null | undefined) =>
    v == null ? "—" : Number(v).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

  return (
    <div className="p-4 space-y-6">

      {/* ── Passthrough Rules ─────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-semibold text-slate-700">Passthrough Rules</div>
            <div className="text-xs text-slate-500 mt-0.5">
              When an OCO is approved, draft sub CCOs are auto-generated for each active rule.
            </div>
          </div>
          <button
            className="bg-indigo-600 text-white text-xs px-3 py-1.5 rounded hover:bg-indigo-700"
            onClick={() => setShowAddRule(true)}
          >
            + Add Rule
          </button>
        </div>

        {showAddRule && (
          <div className="border border-indigo-200 rounded-lg p-4 bg-indigo-50 space-y-3 mb-4">
            <div className="text-sm font-semibold text-indigo-800">New Passthrough Rule</div>
            <div className="flex gap-3 flex-wrap">
              <div>
                <label className="text-xs text-slate-600 block mb-1">Subcontract *</label>
                <select
                  className="border rounded px-2 py-1.5 text-sm bg-white min-w-48"
                  value={ruleForm.subcontract_id}
                  onChange={(e) => setRuleForm({ ...ruleForm, subcontract_id: e.target.value })}
                >
                  <option value="">— select —</option>
                  {subs?.map((s) => (
                    <option key={s.id} value={s.id}>{s.subcontract_no}{s.description ? ` — ${s.description}` : ""}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-600 block mb-1">Markup %</label>
                <input
                  type="number"
                  className="border rounded px-2 py-1 text-sm w-20"
                  value={ruleForm.markup_percent}
                  min={0}
                  step={0.5}
                  onChange={(e) => setRuleForm({ ...ruleForm, markup_percent: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs text-slate-600 block mb-1">Phase filter</label>
                <select
                  className="border rounded px-2 py-1.5 text-sm bg-white min-w-36"
                  value={ruleForm.phase_id}
                  onChange={(e) => setRuleForm({ ...ruleForm, phase_id: e.target.value })}
                >
                  <option value="">any phase</option>
                  {phases?.map((p) => (
                    <option key={p.id} value={p.id}>{p.code} {p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-600 block mb-1">Cost type filter</label>
                <select
                  className="border rounded px-2 py-1.5 text-sm bg-white"
                  value={ruleForm.cost_type}
                  onChange={(e) => setRuleForm({ ...ruleForm, cost_type: e.target.value })}
                >
                  <option value="">any type</option>
                  {["material", "labor", "subcontract", "equipment", "other", "burden"].map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1 min-w-40">
                <label className="text-xs text-slate-600 block mb-1">Notes</label>
                <input
                  type="text"
                  className="border rounded px-2 py-1 text-sm w-full"
                  value={ruleForm.notes}
                  onChange={(e) => setRuleForm({ ...ruleForm, notes: e.target.value })}
                />
              </div>
            </div>
            {ruleError && <div className="text-xs text-red-600">{ruleError}</div>}
            <div className="flex gap-2">
              <button
                className="bg-indigo-600 text-white text-xs px-3 py-1.5 rounded hover:bg-indigo-700 disabled:opacity-50"
                onClick={saveRule}
                disabled={savingRule || !ruleForm.subcontract_id}
              >
                {savingRule ? "Saving…" : "Save Rule"}
              </button>
              <button
                className="text-xs px-3 py-1.5 rounded border hover:bg-slate-50"
                onClick={() => { setShowAddRule(false); setRuleError(null); }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {rulesLoading ? (
          <div className="text-sm text-slate-400">Loading…</div>
        ) : !rules || rules.length === 0 ? (
          <div className="text-sm text-slate-400 italic">No passthrough rules yet. Add one to auto-generate sub CCOs on OCO approval.</div>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: "#f8fafc" }}>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Subcontract</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Phase filter</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Cost type filter</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Markup %</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Notes</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Active</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ fontSize: 12 }}>{subName(r.subcontract_id)}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{phaseName(r.phase_id)}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{r.cost_type ?? <span className="text-slate-400 italic">any</span>}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }} align="right">
                    {Number(r.markup_percent).toFixed(1)}%
                  </TableCell>
                  <TableCell sx={{ fontSize: 11, color: "#94a3b8" }}>{r.notes ?? "—"}</TableCell>
                  <TableCell>
                    <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${r.is_active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                      {r.is_active ? "ON" : "OFF"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <button
                      className="text-xs text-red-500 hover:text-red-700"
                      onClick={() => deleteRule(r.id)}
                    >
                      Delete
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* ── Passthrough History ───────────────────────────────── */}
      <div>
        <div className="text-sm font-semibold text-slate-700 mb-2">View / Generate Passthrough</div>
        <div className="flex flex-wrap gap-3 items-end mb-3">
          <div>
            <label className="text-xs text-slate-600 block mb-1">Owner CO (OCO)</label>
            <select
              className="border rounded px-2 py-1.5 text-sm bg-white min-w-56"
              value={selectedCOId}
              onChange={(e) => { setSelectedCOId(e.target.value); setGenError(null); }}
            >
              <option value="">— select approved OCO —</option>
              {approvedOCOs.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.co_number}{c.description ? ` — ${c.description.slice(0, 40)}` : ""}
                </option>
              ))}
            </select>
          </div>
          {selectedCOId && (
            <button
              className="bg-slate-700 text-white text-xs px-3 py-1.5 rounded hover:bg-slate-900 disabled:opacity-50"
              onClick={runGenerate}
              disabled={generating}
            >
              {generating ? "Generating…" : "Generate / Re-run"}
            </button>
          )}
        </div>
        {genError && <div className="text-xs text-amber-600 mb-2">{genError}</div>}

        {selectedCOId && (
          ptLoading ? (
            <div className="text-sm text-slate-400">Loading…</div>
          ) : !coPassthroughs || coPassthroughs.length === 0 ? (
            <div className="text-sm text-slate-400 italic">No passthrough CCOs generated yet for this OCO.</div>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: "#f8fafc" }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Sub CCO #</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Amount</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>How triggered</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Generated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {coPassthroughs.map((pt) => (
                  <TableRow key={pt.id} hover>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{pt.sub_co_number ?? "—"}</TableCell>
                    <TableCell>{statusChip(pt.sub_co_status)}</TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }} align="right">
                      {fmt(pt.sub_co_contract_amount)}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{pt.sub_co_description ?? "—"}</TableCell>
                    <TableCell sx={{ fontSize: 11, color: "#94a3b8" }}>
                      {pt.triggered_by ? "Manual" : "Auto on approval"}
                    </TableCell>
                    <TableCell sx={{ fontSize: 11, color: "#94a3b8" }}>
                      {pt.created_at ? new Date(pt.created_at).toLocaleString() : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        )}
      </div>
    </div>
  );
}

// ── Approval tab (CONSTRUCTION-BUDGET-APPROVAL-001) ──────────────────────
// Multi-step named-approver routing for the job budget. Reads the current
// approval state, shows the approver chain + immutable audit trail, and lets
// the owner submit / recall / unlock. Backend endpoints land in Inc 1–2; until
// then a missing endpoint is treated as an un-submitted (draft) budget.
const APPROVER_ROLES = [
  { value: "pm", label: "Project Manager" },
  { value: "controller", label: "Controller" },
  { value: "exec", label: "Executive" },
  { value: "owner_rep", label: "Owner's Rep" },
  { value: "other", label: "Other" },
];

function statusChip(status: BudgetApprovalState["budget_status"], current?: number | null, count?: number | null) {
  if (status === "approved") {
    return <Chip size="small" icon={<LockOutlinedIcon sx={{ fontSize: 15 }} />} label="Approved · locked"
      sx={{ bgcolor: "success.light", color: "success.dark", fontWeight: 600 }} />;
  }
  if (status === "pending_approval") {
    return <Chip size="small" icon={<ScheduleOutlinedIcon sx={{ fontSize: 15 }} />}
      label={count ? `Pending approval · Tier ${current ?? 1} of ${count}` : "Pending approval"}
      color="warning" variant="outlined" sx={{ fontWeight: 600 }} />;
  }
  return <Chip size="small" label="Draft — not submitted" variant="outlined"
    sx={{ fontWeight: 600, color: "text.secondary" }} />;
}

function ApproverNode({ status, n }: { status: ApproverStatus; n: number }) {
  const base = "flex-none w-7 h-7 rounded-full grid place-items-center text-xs font-semibold border-2 z-10 ";
  if (status === "approved")
    return <div className={base + "bg-emerald-50 border-emerald-500 text-emerald-600 dark:bg-emerald-950"}><CheckCircleOutlineIcon sx={{ fontSize: 16 }} /></div>;
  if (status === "current")
    return <div className={base + "bg-amber-50 border-amber-500 text-amber-700 dark:bg-amber-950"}><ScheduleOutlinedIcon sx={{ fontSize: 16 }} /></div>;
  if (status === "rejected")
    return <div className={base + "bg-red-50 border-red-400 text-red-600 dark:bg-red-950"}>!</div>;
  return <div className={base + "bg-slate-50 border-slate-300 text-slate-400 dark:bg-gray-800 dark:border-gray-600"}>{n}</div>;
}

function EventIcon({ kind }: { kind: string }) {
  const cls = "flex-none w-6 h-6 rounded-full grid place-items-center border mt-0.5 ";
  const map: Record<string, { c: string; el: React.ReactNode }> = {
    submitted: { c: "text-amber-700 border-amber-300 bg-amber-50 dark:bg-amber-950", el: <ArrowUpwardIcon sx={{ fontSize: 13 }} /> },
    emailed: { c: "text-slate-500 border-slate-200 bg-slate-50 dark:bg-gray-800 dark:border-gray-700", el: <MailOutlineIcon sx={{ fontSize: 13 }} /> },
    approved: { c: "text-emerald-600 border-emerald-300 bg-emerald-50 dark:bg-emerald-950", el: <CheckCircleOutlineIcon sx={{ fontSize: 13 }} /> },
    comment: { c: "text-amber-700 border-amber-300 bg-amber-50 dark:bg-amber-950", el: <EditOutlinedIcon sx={{ fontSize: 13 }} /> },
    excel_returned: { c: "text-sky-600 border-sky-300 bg-sky-50 dark:bg-sky-950", el: <TableChartOutlinedIcon sx={{ fontSize: 13 }} /> },
    rejected: { c: "text-red-600 border-red-300 bg-red-50 dark:bg-red-950", el: <span className="text-xs font-bold">!</span> },
    recalled: { c: "text-slate-500 border-slate-200 bg-slate-50 dark:bg-gray-800 dark:border-gray-700", el: <LockOpenOutlinedIcon sx={{ fontSize: 13 }} /> },
    unlocked: { c: "text-slate-500 border-slate-200 bg-slate-50 dark:bg-gray-800 dark:border-gray-700", el: <LockOpenOutlinedIcon sx={{ fontSize: 13 }} /> },
  };
  const m = map[kind] ?? { c: "text-slate-500 border-slate-200 bg-slate-50 dark:bg-gray-800 dark:border-gray-700", el: <ScheduleOutlinedIcon sx={{ fontSize: 13 }} /> };
  return <div className={cls + m.c}>{m.el}</div>;
}

function BudgetApprovalTab({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [submitOpen, setSubmitOpen] = useState(false);

  const q = useQuery({
    queryKey: [...projectKeys.all, "budget-approval", projectId],
    queryFn: () => api<BudgetApprovalState>(`/api/projects/${projectId}/budget-approval`),
    enabled: Boolean(projectId),
    retry: false,
  });

  const money = (v: string | number) =>
    `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  // Pre-backend / no-request fallback: render as an un-submitted draft.
  const state: BudgetApprovalState = q.data ?? {
    budget_status: "draft", request_id: null, current_tier: null, tier_count: null,
    budget_total: "0", approvers: [], timeline: [], approved_at: null, approved_by_name: null,
  };

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: [...projectKeys.all, "budget-approval", projectId] });

  const recall = useMutation({
    mutationFn: () => api(`/api/projects/${projectId}/budget-approval/recall`, { method: "POST" }),
    onSuccess: () => { toast({ message: "Approval recalled — budget back to draft", severity: "success" }); invalidate(); },
    onError: (e: unknown) => toast({ message: errorMessage(e), severity: "error" }),
  });
  const unlock = useMutation({
    mutationFn: () => api(`/api/projects/${projectId}/budget-approval/unlock`, { method: "POST" }),
    onSuccess: () => { toast({ message: "Budget unlocked — back to draft", severity: "success" }); invalidate(); },
    onError: (e: unknown) => toast({ message: errorMessage(e), severity: "error" }),
  });

  if (q.isLoading) return <CircularProgress size={20} />;

  return (
    <div className="max-w-4xl">
      <div className="flex items-center gap-3 mb-1">
        <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Budget Approval</h2>
        <HelpHint docPath="job-budgets/professional-controls.md" label="Approval guide" />
        <div className="flex-1" />
        {statusChip(state.budget_status, state.current_tier, state.tier_count)}
      </div>
      <p className="text-xs text-slate-400 italic mb-4">
        Route the job budget to named approvers. Each gets an email to review, comment, and approve; the
        budget locks only when the last approver signs off. Every step is audited.
      </p>

      {/* ── DRAFT — not submitted ───────────────────────────────── */}
      {state.budget_status === "draft" && (
        <Paper variant="outlined" className="p-6 text-center">
          <SendOutlinedIcon sx={{ fontSize: 30, color: "text.disabled" }} />
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
            This budget hasn't been submitted for approval.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, mb: 2 }}>
            Submit it to a chain of named approvers. The original locks as a baseline while it routes.
          </Typography>
          <Button variant="contained" startIcon={<SendOutlinedIcon />} onClick={() => setSubmitOpen(true)}>
            Submit for approval
          </Button>
        </Paper>
      )}

      {/* ── PENDING / APPROVED — chain + audit ──────────────────── */}
      {state.budget_status !== "draft" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Approval route */}
          <Paper variant="outlined">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 dark:border-gray-800">
              <Typography variant="subtitle2">Approval route</Typography>
              <span className="ml-auto text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                {state.approvers.length} named approvers
              </span>
            </div>
            <div className="px-4 py-2">
              {state.approvers.map((a, i) => (
                <div key={a.tier_index} className="flex gap-3 py-2">
                  <div className="flex flex-col items-center">
                    <ApproverNode status={a.status} n={a.tier_index + 1} />
                    {i < state.approvers.length - 1 && <div className="w-0.5 flex-1 bg-slate-200 dark:bg-gray-700 my-1" />}
                  </div>
                  <div className="flex-1 pb-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[13px] font-semibold text-slate-800 dark:text-slate-200">{a.name}</span>
                      {a.external && (
                        <span className="text-[10px] font-semibold text-sky-600 bg-sky-50 border border-sky-200 rounded px-1.5 dark:bg-sky-950">EXTERNAL</span>
                      )}
                      {a.status === "approved" && <Chip size="small" label="Approved" sx={{ height: 18, fontSize: 10, bgcolor: "success.light", color: "success.dark" }} />}
                      {a.status === "current" && <Chip size="small" label="Reviewing now" color="warning" variant="outlined" sx={{ height: 18, fontSize: 10 }} />}
                      {a.status === "waiting" && <Chip size="small" label="Waiting" variant="outlined" sx={{ height: 18, fontSize: 10, color: "text.secondary" }} />}
                      {a.status === "rejected" && <Chip size="small" label="Requested changes" color="error" variant="outlined" sx={{ height: 18, fontSize: 10 }} />}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {APPROVER_ROLES.find((r) => r.value === a.role)?.label ?? a.role}
                      {a.acted_at ? ` · ${new Date(a.acted_at).toLocaleDateString()}` : a.email ? ` · ${a.email}` : ""}
                    </div>
                    {a.note && (
                      <div className="mt-1.5 text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-gray-800/60 border border-slate-100 dark:border-gray-700 border-l-2 rounded-r px-2.5 py-1.5">
                        “{a.note}”
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100 dark:border-gray-800">
                <span className="text-xs text-slate-500">
                  Routing budget of <span className="font-semibold tabular-nums text-slate-700 dark:text-slate-300">{money(state.budget_total)}</span>
                </span>
                <div className="flex-1" />
                {state.budget_status === "pending_approval" && (
                  <Button size="small" color="inherit" onClick={() => recall.mutate()} disabled={recall.isPending}>
                    Recall submission
                  </Button>
                )}
                {state.budget_status === "approved" && (
                  <Button size="small" startIcon={<LockOpenOutlinedIcon />} onClick={() => unlock.mutate()} disabled={unlock.isPending}>
                    Unlock to re-baseline
                  </Button>
                )}
              </div>
            </div>
          </Paper>

          {/* Audit trail */}
          <Paper variant="outlined">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 dark:border-gray-800">
              <Typography variant="subtitle2">Audit trail</Typography>
              <span className="ml-auto text-[11px] uppercase tracking-wide text-slate-400 font-semibold">immutable</span>
            </div>
            <div className="px-4 py-3">
              {state.timeline.length === 0 ? (
                <Typography variant="caption" color="text.secondary">No activity yet.</Typography>
              ) : (
                state.timeline.map((e) => (
                  <div key={e.id} className="flex gap-3 py-1.5 text-[13px]">
                    <EventIcon kind={e.kind} />
                    <div>
                      <div className="text-slate-700 dark:text-slate-300">
                        <b>{e.detail ?? e.kind}</b>{e.actor ? <> · {e.actor}</> : null}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-0.5">{new Date(e.at).toLocaleString()}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Paper>
        </div>
      )}

      <ProposedChangesPanel projectId={projectId} onApplied={invalidate} />

      <Alert severity="info" icon={<InfoOutlinedIcon fontSize="inherit" />} sx={{ mt: 3, fontSize: 12 }}>
        Once submitted, the original is a <b>locked baseline</b> — edits route through this approval or a
        change order (net-zero transfers still go through). The budget applies and locks only when every
        approver signs off.
      </Alert>

      {submitOpen && (
        <SubmitForApprovalDialog
          projectId={projectId}
          onClose={() => setSubmitOpen(false)}
          onSubmitted={() => { setSubmitOpen(false); invalidate(); }}
        />
      )}
    </div>
  );
}

interface ProposedChange {
  id: string;
  cost_type: string;
  current_amount: string;
  proposed_amount: string;
  rationale: string | null;
  source_comment: string | null;
  proposed_by: string | null;
  created_at: string | null;
}

// Inc 4b/5 — reviewer comments the agent drafted into budget changes; accounting
// confirms (applies an audited revision) or discards. Hidden when there are none.
function ProposedChangesPanel({ projectId, onApplied }: { projectId: string; onApplied: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({
    queryKey: [...projectKeys.all, "budget-proposed-changes", projectId],
    queryFn: () => api<ProposedChange[]>(`/api/projects/${projectId}/budget-proposed-changes`),
    enabled: Boolean(projectId),
    retry: false,
  });
  const money = (v: string | number) => `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: [...projectKeys.all, "budget-proposed-changes", projectId] });
    qc.invalidateQueries({ queryKey: [...projectKeys.all, "budgets", projectId] });
    onApplied();
  };
  const act = useMutation({
    mutationFn: ({ id, kind }: { id: string; kind: "confirm" | "discard" }) =>
      api(`/api/projects/${projectId}/budget-proposed-changes/${id}/${kind}`, { method: "POST" }),
    onSuccess: (_d, v) => {
      toast({ message: v.kind === "confirm" ? "Change applied to the budget" : "Draft discarded", severity: "success" });
      invalidate();
    },
    onError: (e: unknown) => toast({ message: errorMessage(e), severity: "error" }),
  });

  const drafts = q.data ?? [];
  if (drafts.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ mt: 3 }}>
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 dark:border-gray-800">
        <Typography variant="subtitle2">Proposed changes from reviewers</Typography>
        <Chip size="small" label={`${drafts.length} pending`} color="warning" variant="outlined" sx={{ height: 20 }} />
        <span className="ml-auto text-[11px] uppercase tracking-wide text-slate-400 font-semibold">agent-drafted · confirm to apply</span>
      </div>
      <div>
        {drafts.map((d) => {
          const up = Number(d.proposed_amount) > Number(d.current_amount);
          return (
            <div key={d.id} className="flex gap-4 items-start px-4 py-3 border-b border-slate-100 dark:border-gray-800 last:border-0 flex-wrap">
              <span className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded text-sky-700 bg-sky-50 border border-sky-200 dark:bg-sky-950 whitespace-nowrap">
                {d.proposed_by ?? "Reviewer"}
              </span>
              <div className="flex-1 min-w-[220px]">
                <div className="text-[13px] font-semibold capitalize">{d.cost_type}</div>
                <div className="mt-1 text-[13px] tabular-nums flex items-center gap-2 flex-wrap">
                  <span className="text-slate-400 line-through">{money(d.current_amount)}</span>
                  <span className="text-slate-400">→</span>
                  <span className="font-semibold">{money(d.proposed_amount)}</span>
                  <span className={up ? "text-red-600 text-[11px] font-semibold" : "text-emerald-700 text-[11px] font-semibold"}>
                    {up ? "▲" : "▼"} {money(Math.abs(Number(d.proposed_amount) - Number(d.current_amount)))}
                  </span>
                </div>
                {d.rationale && <div className="mt-1 text-xs text-slate-500 dark:text-slate-400 italic">“{d.rationale}”</div>}
              </div>
              <div className="flex gap-2 self-center">
                <Button size="small" variant="outlined" color="success" disabled={act.isPending}
                  onClick={() => act.mutate({ id: d.id, kind: "confirm" })}>Confirm &amp; apply</Button>
                <Button size="small" color="inherit" disabled={act.isPending}
                  onClick={() => act.mutate({ id: d.id, kind: "discard" })}>Discard</Button>
              </div>
            </div>
          );
        })}
      </div>
    </Paper>
  );
}

function SubmitForApprovalDialog({
  projectId, onClose, onSubmitted,
}: { projectId: string; onClose: () => void; onSubmitted: () => void }) {
  const toast = useToast();
  const [rows, setRows] = useState<{ name: string; email: string; role: string; external: boolean }[]>([
    { name: "", email: "", role: "pm", external: false },
  ]);

  const submit = useMutation({
    mutationFn: () =>
      api(`/api/projects/${projectId}/budget-approval/submit`, {
        method: "POST",
        body: { approvers: rows.map((r, i) => ({ ...r, tier_index: i })) },
      }),
    onSuccess: () => { toast({ message: "Budget submitted for approval", severity: "success" }); onSubmitted(); },
    onError: (e: unknown) => toast({ message: errorMessage(e), severity: "error" }),
  });

  const valid = rows.length > 0 && rows.every((r) => r.name.trim() && /.+@.+\..+/.test(r.email));
  const setRow = (i: number, patch: Partial<(typeof rows)[number]>) =>
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Submit budget for approval</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
          Add approvers in the order they should review. Each gets a secure email link to review, comment,
          and approve — external approvers don't need a Bynaus login.
        </Typography>
        <Stack spacing={1.5}>
          {rows.map((r, i) => (
            <div key={i} className="flex gap-2 items-center">
              <span className="text-xs text-slate-400 w-4 tabular-nums">{i + 1}</span>
              <TextField size="small" label="Name" value={r.name}
                onChange={(e) => setRow(i, { name: e.target.value })} sx={{ flex: 1 }} />
              <TextField size="small" label="Email" value={r.email}
                onChange={(e) => setRow(i, { email: e.target.value })} sx={{ flex: 1.3 }} />
              <TextField size="small" select label="Role" value={r.role}
                onChange={(e) => setRow(i, { role: e.target.value })} sx={{ width: 130 }}>
                {APPROVER_ROLES.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
              <Tooltip title="External (no login)">
                <FormControlLabel sx={{ m: 0 }}
                  control={<Switch size="small" checked={r.external} onChange={(e) => setRow(i, { external: e.target.checked })} />}
                  label="" />
              </Tooltip>
              <IconButton size="small" disabled={rows.length === 1}
                onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </div>
          ))}
        </Stack>
        <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1.5 }}
          onClick={() => setRows((rs) => [...rs, { name: "", email: "", role: "controller", external: false }])}>
          Add approver
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button variant="contained" startIcon={<SendOutlinedIcon />} disabled={!valid || submit.isPending}
          onClick={() => submit.mutate()}>
          Submit &amp; send emails
        </Button>
      </DialogActions>
    </Dialog>
  );
}
